import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { fr as frLocale } from "date-fns/locale";
import { format } from "date-fns";
import { Upload, X, Star, GripVertical, Pencil, RefreshCw, ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Trash2, Check } from "lucide-react";
import DateInput from "@/components/DateInput";
import { Link, useSearchParams } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend, BarChart, Bar } from "recharts";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/AuthContext";
import { useLang, STATUS_KEYS, SECTORS } from "@/i18n";
import LoginForm from "@/components/LoginForm";
import StatusBadge from "@/components/StatusBadge";
import SiteContentTab from "@/components/SiteContentTab";

const EMPTY_PROP = {
  name: "", location: "", type: "villa", guests: 4, bedrooms: 2, pool: false, surface: "",
  short_fr: "", short_en: "", desc_fr: "", desc_en: "", images: [], blocked_ranges: [],
  has_video: false, video_url: "", video_file: "",
  has_airbnb: false, airbnb_url: "", airbnb_ical_url: "", airbnb_ranges_count: 0, airbnb_synced_at: "",
  external_sync_url: "", external_ranges_count: 0, external_synced_at: "", weekly_price: "", nightly_price: "",
  dissociate_rooms: false, photo_groups: {}, cover_image: "",
  price_periods: [], extra_person_fee: "", cleaning_fee: "",
  short_stay_discount: "", short_stay_max_nights: "", long_stay_discount: "", long_stay_min_nights: "",
  has_extra_person_fee: false, has_cleaning_fee: false, has_short_discount: false, has_long_discount: false,
};

const ROOM_ORDER = ["sejour", "bureau", "salle_a_manger", "cuisine", "chambre_1", "salle_de_bain_1", "chambre_2", "salle_de_bain_2", "chambre_3", "salle_de_bain_3", "chambre_4", "salle_de_bain_4", "chambre_5", "salle_de_bain_5", "exterieur"];
const ROOM_BASE = ["sejour", "bureau", "salle_a_manger", "cuisine", "chambre_1", "salle_de_bain_1", "chambre_2", "salle_de_bain_2"];
const ROOM_EXTRA = [["chambre_3", "salle_de_bain_3"], ["chambre_4", "salle_de_bain_4"], ["chambre_5", "salle_de_bain_5"]];

const CARO_EMAIL = "caropv2a@gmail.com";
const MARGOT_EMAIL = "margot.bourbasquet@hotmail.fr";
const MARGOT_STATS_START = "2026-09-01";

const STATUS_COLORS = { EN_ATTENTE: "#CFC5B1", VALIDEE: "#4B5840", REFUSEE: "#B08D57", EN_COURS: "#50626C", TERMINEE: "#2A3323" };

function propToForm(p) {
  return {
    name: p.name, location: p.location, type: p.type, guests: p.guests, bedrooms: p.bedrooms, pool: p.pool,
    surface: p.surface || "",
    short_fr: p.short_description?.fr || "", short_en: p.short_description?.en || "",
    desc_fr: p.description?.fr || "", desc_en: p.description?.en || "",
    images: p.images || [],
    blocked_ranges: p.blocked_ranges || [],
    has_video: !!(p.video_url || p.video_file),
    video_url: (p.video_url || "").startsWith("/api/files/") ? "" : p.video_url || "",
    video_file: p.video_file || ((p.video_url || "").startsWith("/api/files/") ? p.video_url : ""),
    has_airbnb: !!p.airbnb_url,
    airbnb_url: p.airbnb_url || "",
    airbnb_ical_url: p.airbnb_ical_url || "",
    airbnb_ranges_count: (p.airbnb_ranges || []).length,
    airbnb_synced_at: p.airbnb_synced_at || "",
    external_sync_url: p.external_sync_url || "",
    external_ranges_count: (p.external_ranges || []).length,
    external_synced_at: p.external_synced_at || "",
    weekly_price: p.weekly_price ?? "",
    nightly_price: p.nightly_price ?? "",
    dissociate_rooms: !!p.dissociate_rooms,
    photo_groups: p.photo_groups || {},
    cover_image: p.cover_image || "",
    price_periods: p.price_periods || [],
    extra_person_fee: p.extra_person_fee ?? "",
    cleaning_fee: p.cleaning_fee ?? "",
    short_stay_discount: p.short_stay_discount ?? "",
    short_stay_max_nights: p.short_stay_max_nights ?? "",
    long_stay_discount: p.long_stay_discount ?? "",
    long_stay_min_nights: p.long_stay_min_nights ?? "",
    has_extra_person_fee: p.extra_person_fee != null,
    has_cleaning_fee: p.cleaning_fee != null,
    has_short_discount: p.short_stay_discount != null,
    has_long_discount: p.long_stay_discount != null,
  };
}

function formToPayload(f) {
  return {
    name: f.name.trim(),
    location: f.location.trim(),
    type: f.type,
    guests: Number(f.guests) || 2,
    bedrooms: Number(f.bedrooms) || 1,
    pool: !!f.pool,
    surface: Number(f.surface) || null,
    short_description: { fr: f.short_fr, en: f.short_en },
    description: { fr: f.desc_fr, en: f.desc_en },
    images: f.dissociate_rooms ? ROOM_ORDER.flatMap((k) => f.photo_groups[k] || []) : f.images,
    dissociate_rooms: !!f.dissociate_rooms,
    photo_groups: f.dissociate_rooms ? f.photo_groups : {},
    cover_image: f.cover_image || "",
    price_periods: f.price_periods,
    extra_person_fee: f.has_extra_person_fee && f.extra_person_fee !== "" ? Number(f.extra_person_fee) : null,
    cleaning_fee: f.has_cleaning_fee && f.cleaning_fee !== "" ? Number(f.cleaning_fee) : null,
    short_stay_discount: f.has_short_discount && f.short_stay_discount !== "" ? Number(f.short_stay_discount) : null,
    short_stay_max_nights: f.has_short_discount && f.short_stay_max_nights !== "" ? Number(f.short_stay_max_nights) : null,
    long_stay_discount: f.has_long_discount && f.long_stay_discount !== "" ? Number(f.long_stay_discount) : null,
    long_stay_min_nights: f.has_long_discount && f.long_stay_min_nights !== "" ? Number(f.long_stay_min_nights) : null,
    video_url: f.has_video ? (f.video_url || "").trim() : "",
    video_file: f.has_video ? f.video_file : "",
    airbnb_url: f.has_airbnb ? (f.airbnb_url || "").trim() : "",
    airbnb_ical_url: f.has_airbnb ? (f.airbnb_ical_url || "").trim() : "",
    external_sync_url: (f.external_sync_url || "").trim(),
    weekly_price: f.weekly_price !== "" && f.weekly_price != null ? Number(f.weekly_price) : null,
    nightly_price: f.nightly_price !== "" && f.nightly_price != null ? Number(f.nightly_price) : null,
    blocked_ranges: f.blocked_ranges,
  };
}

function PropertyForm({ initial, onSave, onCancel, onDelete, propertyId, onBookingsChanged, t }) {
  const { lang } = useLang();
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [syncingAirbnb, setSyncingAirbnb] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const dragRef = useRef(null);
  const [extraRooms, setExtraRooms] = useState(() => {
    const g = initial?.photo_groups || {};
    if ((g.chambre_5 || []).length || (g.salle_de_bain_5 || []).length) return 3;
    if ((g.chambre_4 || []).length || (g.salle_de_bain_4 || []).length) return 2;
    if ((g.chambre_3 || []).length || (g.salle_de_bain_3 || []).length) return 1;
    return 0;
  });
  const visibleRooms = [...ROOM_BASE, ...ROOM_EXTRA.slice(0, extraRooms).flat(), "exterieur"];
  const [range, setRange] = useState();
  const [guestFirst, setGuestFirst] = useState("");
  const [guestLast, setGuestLast] = useState("");
  const [priceValue, setPriceValue] = useState("");
  const [priceWeekValue, setPriceWeekValue] = useState("");
  const [tenantFirst, setTenantFirst] = useState("");
  const [tenantLast, setTenantLast] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [tenantAmount, setTenantAmount] = useState("");
  const [simArrival, setSimArrival] = useState("");
  const [simDeparture, setSimDeparture] = useState("");
  const [simGuests, setSimGuests] = useState("");
  const [simResult, setSimResult] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const fmtDay = (iso) => {
    try {
      return format(new Date(iso + "T12:00:00"), "dd MMM yyyy", { locale: lang === "fr" ? frLocale : undefined });
    } catch {
      return iso;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formToPayload(f));
    } finally {
      setSaving(false);
    }
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      setF((s) => ({ ...s, images: [...s.images, ...urls] }));
      if (propertyId) {
        await api.put(`/admin/properties/${propertyId}`, formToPayload({ ...f, images: [...f.images, ...urls] }));
        toast.success(t.admin.updated);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((s) => ({ ...s, video_file: data.url }));
      toast.success(t.admin.video_uploaded);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => setF((s) => ({ ...s, images: s.images.filter((_, j) => j !== i) }));

  const setCover = (url) => setF((s) => ({ ...s, cover_image: url }));

  const uploadRoomImages = async (key, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setF((s) => ({
          ...s,
          photo_groups: { ...s.photo_groups, [key]: [...(s.photo_groups[key] || []), data.url] },
        }));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeRoomImage = (key, i) =>
    setF((s) => ({
      ...s,
      photo_groups: { ...s.photo_groups, [key]: (s.photo_groups[key] || []).filter((_, j) => j !== i) },
    }));

  const dropImage = (i) => {
    const from = dragRef.current;
    if (from === null || from === i) return;
    dragRef.current = null;
    setDragIdx(null);
    setF((s) => {
      const arr = [...s.images];
      const [moved] = arr.splice(from, 1);
      arr.splice(i, 0, moved);
      return { ...s, images: arr };
    });
  };

  const dropRoomImage = (key, i) => {
    const from = dragRef.current;
    dragRef.current = null;
    setDragIdx(null);
    if (!from || from.key !== key || from.i === i) return;
    setF((s) => {
      const arr = [...(s.photo_groups[key] || [])];
      const [moved] = arr.splice(from.i, 1);
      arr.splice(i, 0, moved);
      return { ...s, photo_groups: { ...s.photo_groups, [key]: arr } };
    });
  };

  const addRange = () => {
    if (!range?.from) return;
    const start = format(range.from, "yyyy-MM-dd");
    const end = range.to ? format(range.to, "yyyy-MM-dd") : start;
    setF((s) => ({
      ...s,
      blocked_ranges: [
        ...s.blocked_ranges,
        { start, end, guest_first_name: guestFirst.trim(), guest_last_name: guestLast.trim() },
      ].sort((a, b) => a.start.localeCompare(b.start)),
    }));
    setRange(undefined);
    setGuestFirst("");
    setGuestLast("");
  };

  const addTenant = async () => {
    if (!range?.from || !range?.to || !propertyId) return;
    const start = format(range.from, "yyyy-MM-dd");
    const end = format(range.to, "yyyy-MM-dd");
    try {
      await api.post("/admin/bookings", {
        property_id: propertyId,
        first_name: tenantFirst.trim(),
        last_name: tenantLast.trim(),
        email: tenantEmail.trim(),
        phone: tenantPhone.trim(),
        arrival: start,
        departure: end,
        amount: tenantAmount !== "" ? Number(tenantAmount) : undefined,
      });
      setF((s) => ({
        ...s,
        blocked_ranges: [
          ...s.blocked_ranges,
          { start, end, guest_first_name: tenantFirst.trim(), guest_last_name: tenantLast.trim() },
        ].sort((a, b) => a.start.localeCompare(b.start)),
      }));
      setRange(undefined);
      setTenantFirst("");
      setTenantLast("");
      setTenantEmail("");
      setTenantPhone("");
      setTenantAmount("");
      onBookingsChanged?.();
      toast.success(t.admin.tenant_added);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeRange = (i) =>
    setF((s) => ({ ...s, blocked_ranges: s.blocked_ranges.filter((_, j) => j !== i) }));

  const editRange = (i) => {
    const r = f.blocked_ranges[i];
    if (!r) return;
    setRange({ from: new Date(r.start + "T12:00:00"), to: new Date(r.end + "T12:00:00") });
    setGuestFirst(r.guest_first_name || "");
    setGuestLast(r.guest_last_name || "");
    removeRange(i);
  };

  const addPricePeriod = () => {
    if (!range?.from || (priceValue === "" && priceWeekValue === "")) return;
    const start = format(range.from, "yyyy-MM-dd");
    const end = range.to ? format(range.to, "yyyy-MM-dd") : start;
    const entries = [];
    if (priceValue !== "") entries.push({ start, end, price: Number(priceValue), per: "night" });
    if (priceWeekValue !== "") entries.push({ start, end, price: Number(priceWeekValue), per: "week" });
    setF((s) => ({
      ...s,
      price_periods: [...s.price_periods, ...entries].sort((a, b) => a.start.localeCompare(b.start)),
    }));
    setRange(undefined);
    setPriceValue("");
    setPriceWeekValue("");
  };

  const removePricePeriod = (i) =>
    setF((s) => ({ ...s, price_periods: s.price_periods.filter((_, j) => j !== i) }));

  const syncAirbnb = async () => {
    if (!propertyId) return;
    setSyncingAirbnb(true);
    try {
      const { data } = await api.post(`/admin/properties/${propertyId}/sync-airbnb`);
      setF((s) => ({
        ...s,
        airbnb_ranges_count: (data.airbnb_ranges || []).length,
        airbnb_synced_at: data.airbnb_synced_at || "",
        external_ranges_count: (data.external_ranges || []).length,
        external_synced_at: data.external_synced_at || "",
      }));
      toast.success(t.admin.airbnb_synced_ok);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingAirbnb(false);
    }
  };

  const simulate = () => {
    const arr = new Date(simArrival + "T12:00:00");
    const dep = new Date(simDeparture + "T12:00:00");
    if (!simArrival || !simDeparture || isNaN(arr) || isNaN(dep) || dep <= arr) {
      setSimResult({ error: t.admin.sim_invalid });
      return;
    }
    const weekly = Number(f.weekly_price) || 0;
    if (!weekly && !(f.price_periods || []).length) {
      setSimResult({ error: t.admin.sim_no_price });
      return;
    }
    const nights = Math.round((dep - arr) / 86400000);
    let base = 0;
    for (let i = 0; i < nights; i++) {
      const iso = format(arr.getTime() + i * 86400000, "yyyy-MM-dd");
      const pp = (f.price_periods || []).find((p) => p.start <= iso && iso <= p.end);
      base += pp ? (pp.per === "night" ? Number(pp.price) : Number(pp.price) / 7) : weekly / 7;
    }
    let discount = 0;
    let discLabel = "";
    if (f.has_short_discount && f.short_stay_discount !== "" && f.short_stay_max_nights !== "" && nights <= Number(f.short_stay_max_nights)) {
      discount = (base * Number(f.short_stay_discount)) / 100;
      discLabel = t.admin.disc_short;
    } else if (f.has_long_discount && f.long_stay_discount !== "" && f.long_stay_min_nights !== "" && nights >= Number(f.long_stay_min_nights)) {
      discount = (base * Number(f.long_stay_discount)) / 100;
      discLabel = t.admin.disc_long;
    }
    const cleaning = f.has_cleaning_fee && f.cleaning_fee !== "" ? Number(f.cleaning_fee) : 0;
    const extraGuests = Math.max(0, (Number(simGuests) || 0) - 2);
    const extra = f.has_extra_person_fee && f.extra_person_fee !== "" ? Number(f.extra_person_fee) * nights * extraGuests : 0;
    setSimResult({ nights, base, discount, discLabel, cleaning, extra, extraGuests, total: base - discount + cleaning + extra });
  };

  return (
    <form onSubmit={submit} data-testid="admin-property-form" className="mt-4 space-y-4 rounded-md border border-maquis-deep/15 bg-ivoire/60 p-5">
      <div>
        <label className="field-label">{t.admin.video}</label>
        <div className="flex h-12 items-center gap-8">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
            <input
              type="radio"
              name="has_video"
              data-testid="admin-video-yes"
              checked={f.has_video}
              onChange={() => setF((s) => ({ ...s, has_video: true }))}
              className="h-4 w-4 accent-[#4B5840]"
            />
            {t.admin.yes}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
            <input
              type="radio"
              name="has_video"
              data-testid="admin-video-no"
              checked={!f.has_video}
              onChange={() => setF((s) => ({ ...s, has_video: false }))}
              className="h-4 w-4 accent-[#4B5840]"
            />
            {t.admin.no}
          </label>
        </div>
        {f.has_video && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="field-label">{t.admin.video_file}</label>
              <div className="flex flex-wrap items-center gap-4">
                <label
                  data-testid="admin-video-upload"
                  className="btn-outline-elegant inline-flex cursor-pointer items-center gap-2 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                >
                  <Upload size={15} strokeWidth={1.25} />
                  {uploadingVideo ? t.admin.video_uploading : t.admin.video_upload}
                  <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} />
                </label>
                {f.video_file && (
                  <span data-testid="admin-video-uploaded" className="inline-flex items-center gap-2 rounded-full bg-maquis-olive/10 px-4 py-2 text-xs text-maquis-olive">
                    {t.admin.video_uploaded}
                    <button
                      type="button"
                      data-testid="admin-video-file-remove"
                      onClick={() => setF((s) => ({ ...s, video_file: "" }))}
                      aria-label="Retirer la vidéo"
                      className="text-maquis-olive/60 hover:text-maquis-olive"
                    >
                      <X size={13} strokeWidth={2} />
                    </button>
                  </span>
                )}
              </div>
              {f.video_file && (
                <video
                  data-testid="admin-video-preview"
                  src={f.video_file}
                  controls
                  playsInline
                  preload="metadata"
                  className="mt-3 h-32 rounded-md bg-mer"
                />
              )}
            </div>
            <div>
              <label className="field-label">{t.admin.video_url}</label>
              <input
                type="url"
                data-testid="admin-video-url"
                value={f.video_url}
                onChange={set("video_url")}
                placeholder="https://…"
                className="field-input"
              />
            </div>
          </div>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label">{t.admin.name} *</label>
          <input required data-testid="admin-prop-name" value={f.name} onChange={set("name")} className="field-input" />
        </div>
        <div>
          <label className="field-label">{t.admin.location}</label>
          <input
            list="admin-sector-list"
            data-testid="admin-prop-location"
            value={f.location}
            onChange={set("location")}
            placeholder={t.admin.location_placeholder}
            className="field-input"
          />
          <datalist id="admin-sector-list">
            {SECTORS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        <div>
          <label className="field-label">{t.admin.type}</label>
          <select data-testid="admin-prop-type" value={f.type} onChange={set("type")} className="field-input">
            {Object.entries(t.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="min-w-0">
            <label className="field-label"><span className="sm:hidden">{t.admin.spec_guests_short}</span><span className="hidden sm:inline">{t.admin.guests}</span></label>
            <input data-testid="admin-prop-guests" type="number" min="1" value={f.guests} onChange={set("guests")} className="field-input" />
          </div>
          <div className="min-w-0">
            <label className="field-label"><span className="sm:hidden">{t.admin.spec_bedrooms_short}</span><span className="hidden sm:inline">{t.admin.bedrooms}</span></label>
            <input data-testid="admin-prop-bedrooms" type="number" min="0" value={f.bedrooms} onChange={set("bedrooms")} className="field-input" />
          </div>
          <div className="min-w-0">
            <label className="field-label"><span className="sm:hidden">{t.admin.spec_surface_short}</span><span className="hidden sm:inline">{t.admin.surface}</span></label>
            <input data-testid="admin-prop-surface" type="number" min="0" value={f.surface} onChange={set("surface")} className="field-input" />
          </div>
        </div>
        <div className="flex flex-col justify-end gap-3 pb-1">
          <label className="flex items-center gap-2 text-sm text-maquis-deep">
            <input data-testid="admin-prop-pool" type="checkbox" checked={f.pool} onChange={set("pool")} className="h-4 w-4 accent-[#4B5840]" />
            {t.admin.pool}
          </label>
          <label className="flex items-center gap-2 text-sm text-maquis-deep">
            <input data-testid="admin-prop-airbnb" type="checkbox" checked={f.has_airbnb} onChange={set("has_airbnb")} className="h-4 w-4 accent-[#4B5840]" />
            {t.admin.airbnb}
          </label>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label className="field-label">{t.admin.weekly_price}</label>
          <input data-testid="admin-prop-weekly-price" type="number" min="0" value={f.weekly_price} onChange={set("weekly_price")} placeholder="ex. 3500" className="field-input" />
        </div>
        <div className="min-w-0">
          <label className="field-label">{t.admin.nightly_price}</label>
          <input data-testid="admin-prop-nightly-price" type="number" min="0" value={f.nightly_price} onChange={set("nightly_price")} placeholder="ex. 550" className="field-input" />
        </div>
        {f.has_airbnb && (
          <div className="min-w-0 sm:col-span-2 space-y-4">
            <div className="min-w-0">
              <label className="field-label">{t.admin.airbnb_ical_url}</label>
              <input
                type="url"
                data-testid="admin-airbnb-ical-url"
                value={f.airbnb_ical_url}
                onChange={set("airbnb_ical_url")}
                placeholder="https://www.airbnb.fr/calendar/ical/….ics"
                className="field-input"
              />
              <p className="mt-2 text-xs text-maquis-soft">{t.admin.airbnb_ical_hint}</p>
              {f.airbnb_ranges_count > 0 && (
                <p data-testid="admin-airbnb-synced-count" className="mt-2 text-xs font-medium text-maquis-olive">
                  {f.airbnb_ranges_count} {t.admin.airbnb_synced_count}
                  {f.airbnb_synced_at ? ` · ${f.airbnb_synced_at.slice(0, 16).replace("T", " ")}` : ""}
                </p>
              )}
              {propertyId && f.airbnb_ical_url && (
                <button
                  type="button"
                  data-testid="admin-airbnb-sync-now"
                  onClick={syncAirbnb}
                  disabled={syncingAirbnb}
                  className="mt-3 inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-0.5 text-xs uppercase tracking-[0.15em] text-maquis-deep transition-colors hover:border-maquis-deep disabled:opacity-40"
                >
                  <RefreshCw size={13} strokeWidth={1.75} className={syncingAirbnb ? "animate-spin" : ""} />
                  {t.admin.airbnb_sync_now}
                </button>
              )}
            </div>
          </div>
        )}
        <div className="min-w-0 sm:col-span-2">
          <label className="field-label">{t.admin.external_sync_url}</label>
          <input
            type="url"
            data-testid="admin-external-sync-url"
            value={f.external_sync_url}
            onChange={set("external_sync_url")}
            placeholder="URL du calendrier"
            className="field-input"
          />
          <p className="mt-2 text-xs text-maquis-soft max-sm:break-words">{t.admin.external_sync_hint}</p>
          {f.external_ranges_count > 0 && (
            <p data-testid="admin-external-synced-count" className="mt-2 text-xs font-medium text-maquis-olive">
              {f.external_ranges_count} {t.admin.external_synced_count}
              {f.external_synced_at ? ` · ${f.external_synced_at.slice(0, 16).replace("T", " ")}` : ""}
            </p>
          )}
          {propertyId && f.external_sync_url && (
            <button
              type="button"
              data-testid="admin-external-sync-now"
              onClick={syncAirbnb}
              disabled={syncingAirbnb}
              className="mt-3 inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-0.5 text-xs uppercase tracking-[0.15em] text-maquis-deep transition-colors hover:border-maquis-deep disabled:opacity-40"
            >
              <RefreshCw size={13} strokeWidth={1.75} className={syncingAirbnb ? "animate-spin" : ""} />
              {t.admin.airbnb_sync_now}
            </button>
          )}
        </div>
      </div>
      <div className="grid gap-4">
        <div>
          <label className="field-label">{t.admin.short_fr}</label>
          <input data-testid="admin-prop-short-fr" value={f.short_fr} onChange={set("short_fr")} className="field-input" />
        </div>
        <div>
          <label className="field-label">{t.admin.desc_fr}</label>
          <textarea data-testid="admin-prop-desc-fr" rows={3} value={f.desc_fr} onChange={set("desc_fr")} className="field-input resize-y" />
        </div>
      </div>

      <div>
        <label className="field-label">{t.admin.images}</label>
        <p className="mb-3 text-xs text-maquis-soft">{t.admin.drag_hint}</p>
        <label className="mb-4 flex w-fit cursor-pointer items-center gap-2 text-sm text-maquis-deep">
          <input
            type="checkbox"
            data-testid="admin-dissociate-rooms"
            checked={f.dissociate_rooms}
            onChange={(e) => setF((s) => ({ ...s, dissociate_rooms: e.target.checked }))}
            className="h-4 w-4 accent-[#4B5840]"
          />
          {t.admin.dissociate_rooms}
        </label>
        {f.dissociate_rooms ? (
          <div className="space-y-4" data-testid="admin-rooms-sections">
            {visibleRooms.map((key) => (
              <div key={key}>
                {key === "exterieur" && extraRooms < 3 && (
                  <button
                    type="button"
                    data-testid="admin-add-room"
                    onClick={() => setExtraRooms((n) => Math.min(3, n + 1))}
                    className="btn-outline-elegant mb-3 inline-flex items-center gap-2 !px-4 !py-2 text-[11px] uppercase tracking-[0.15em] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                  >
                    {t.admin.add_room}
                  </button>
                )}
              <div data-testid={`admin-room-section-${key}`} className="rounded-md border border-maquis-deep/10 bg-casse p-4">
                <p className="field-label">{t.admin[`room_${key}`]}</p>
                <div className="flex flex-wrap items-center gap-3">
                  {(f.photo_groups[key] || []).map((url, i) => (
                    <div
                      key={url + i}
                      draggable
                      onDragStart={() => {
                        dragRef.current = { key, i };
                        setDragIdx(`${key}-${i}`);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropRoomImage(key, i)}
                      data-testid={`admin-room-image-${key}-${i}`}
                      className={`relative h-20 w-28 cursor-grab overflow-hidden rounded-md border bg-casse shadow-[0_8px_18px_-6px_rgba(42,51,35,0.45)] ${
                        dragIdx === `${key}-${i}` ? "border-maquis-olive opacity-50" : "border-maquis-deep"
                      }`}
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      <GripVertical size={13} strokeWidth={2} className="absolute bottom-1 left-1 text-casse drop-shadow" />
                      {f.cover_image === url && (
                        <span className="absolute left-1 top-1 rounded-full bg-[#B08D57] px-1.5 text-[10px] font-medium text-casse">
                          {t.admin.cover_badge}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setCover(url)}
                        data-testid={`admin-room-cover-${key}-${i}`}
                        aria-label={t.admin.set_cover}
                        className="absolute right-1 top-1 rounded-full bg-mer/50 p-1.5 text-casse transition-colors hover:bg-mer/70"
                      >
                        <Star size={12} strokeWidth={2} className={f.cover_image === url ? "fill-casse" : ""} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRoomImage(key, i)}
                        data-testid={`admin-room-image-remove-${key}-${i}`}
                        className="absolute bottom-1 right-1 rounded-full bg-mer/70 p-1 text-casse hover:bg-mer"
                        aria-label="Supprimer"
                      >
                        <X size={11} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                  <label
                    data-testid={`admin-room-upload-${key}`}
                    className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-maquis-olive/50 text-maquis-olive transition-colors hover:bg-ivoire"
                  >
                    <Upload size={18} strokeWidth={1.25} />
                    <span className="px-1 text-center text-[10px] uppercase tracking-wide">
                      {uploading ? t.admin.uploading : t.admin.upload_images}
                    </span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadRoomImages(key, e)} />
                  </label>
                </div>
              </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3" data-testid="admin-images-list">
              {f.images.map((url, i) => (
                <div
                  key={url + i}
                  draggable
                  onDragStart={() => {
                    dragRef.current = i;
                    setDragIdx(i);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropImage(i)}
                  data-testid={`admin-image-${i}`}
                  className={`relative h-20 w-28 cursor-grab overflow-hidden rounded-md border bg-casse shadow-[0_8px_18px_-6px_rgba(42,51,35,0.45)] ${
                    dragIdx === i ? "border-maquis-olive opacity-50" : "border-maquis-deep"
                  }`}
                >
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <GripVertical size={13} strokeWidth={2} className="absolute bottom-1 left-1 text-casse drop-shadow" />
                  <span className="absolute left-1 top-1 rounded-full bg-casse/85 px-1.5 text-[10px] font-medium text-maquis-deep">
                    {i + 1}
                  </span>
                  {f.cover_image === url && (
                    <span className="absolute left-1 bottom-1 ml-4 rounded-full bg-[#B08D57] px-1.5 text-[10px] font-medium text-casse">
                      {t.admin.cover_badge}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setCover(url)}
                    data-testid={`admin-cover-${i}`}
                    aria-label={t.admin.set_cover}
                    className="absolute right-1 top-1 rounded-full bg-mer/50 p-1.5 text-casse transition-colors hover:bg-mer/70"
                  >
                    <Star size={12} strokeWidth={2} className={f.cover_image === url ? "fill-casse" : ""} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    data-testid={`admin-image-remove-${i}`}
                    className="absolute bottom-1 right-1 rounded-full bg-mer/70 p-1 text-casse hover:bg-mer"
                    aria-label="Supprimer"
                  >
                    <X size={11} strokeWidth={2} />
                  </button>
                </div>
              ))}
              <label
                data-testid="admin-upload-button"
                className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-maquis-olive/50 text-maquis-olive transition-colors hover:bg-ivoire"
              >
                <Upload size={18} strokeWidth={1.25} />
                <span className="px-1 text-center text-[10px] uppercase tracking-wide">
                  {uploading ? t.admin.uploading : t.admin.upload_images}
                </span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} />
              </label>
            </div>
          </>
        )}
      </div>

      <div className="mt-28 border-t border-maquis-deep/10 pt-16">
        <label className="field-label">{t.admin.ranges_title}</label>
        <p className="mb-3 text-xs text-maquis-soft">{t.admin.range_hint}</p>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="w-full max-w-full overflow-x-auto rounded-md border border-maquis-deep/15 bg-casse p-3 lg:w-fit" data-testid="admin-range-calendar">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              locale={lang === "fr" ? frLocale : undefined}
            />
          </div>
          <div className="flex-1 space-y-5">
            {propertyId && (
              <div className="rounded-md border border-maquis-deep/10 bg-casse p-4" data-testid="admin-tenant-section">
                <p className="field-label">{t.admin.tenant_section}</p>
                <p className="mb-3 text-xs leading-relaxed text-maquis-soft">{t.admin.tenant_hint}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input data-testid="admin-tenant-fn" value={tenantFirst} onChange={(e) => setTenantFirst(e.target.value)} placeholder={t.admin.tenant_fn} className="field-input" />
                  <input data-testid="admin-tenant-ln" value={tenantLast} onChange={(e) => setTenantLast(e.target.value)} placeholder={t.admin.tenant_ln} className="field-input" />
                  <input data-testid="admin-tenant-email" type="email" value={tenantEmail} onChange={(e) => setTenantEmail(e.target.value)} placeholder={t.admin.tenant_email} className="field-input" />
                  <input data-testid="admin-tenant-phone" value={tenantPhone} onChange={(e) => setTenantPhone(e.target.value)} placeholder={t.admin.tenant_phone} className="field-input" />
                  <input data-testid="admin-tenant-amount" type="number" min="0" value={tenantAmount} onChange={(e) => setTenantAmount(e.target.value)} placeholder={t.admin.tenant_amount} className="field-input" />
                </div>
                <button
                  type="button"
                  onClick={addTenant}
                  disabled={!range?.from || !range?.to || !tenantFirst.trim() || !tenantLast.trim() || !tenantEmail.trim() || !tenantPhone.trim()}
                  data-testid="admin-add-tenant"
                  className="btn-outline-elegant mt-3 text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-40"
                >
                  {t.admin.add_tenant}
                </button>
              </div>
            )}
            <div className="rounded-md border border-maquis-deep/10 bg-casse p-4">
              <p className="field-label">{t.admin.block_section}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  data-testid="admin-range-guest-fn"
                  value={guestFirst}
                  onChange={(e) => setGuestFirst(e.target.value)}
                  placeholder={t.admin.range_guest_fn}
                  className="field-input"
                />
                <input
                  data-testid="admin-range-guest-ln"
                  value={guestLast}
                  onChange={(e) => setGuestLast(e.target.value)}
                  placeholder={t.admin.range_guest_ln}
                  className="field-input"
                />
              </div>
              <button
                type="button"
                onClick={addRange}
                disabled={!range?.from}
                data-testid="admin-add-range"
                className="btn-outline-elegant mt-3 text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-40"
              >
                {t.admin.add_range}
              </button>
            </div>
            <div className="rounded-md border border-maquis-deep/10 bg-casse p-4">
              <p className="field-label">{t.admin.price_section}</p>
              {range?.from && (
                <>
                  <div className="relative mb-3">
                    <input
                      type="number"
                      min="0"
                      data-testid="admin-price-value"
                      value={priceValue}
                      onChange={(e) => setPriceValue(e.target.value)}
                      placeholder={t.admin.price_placeholder_day}
                      className="field-input pr-9"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-maquis-soft">€</span>
                  </div>
                  <div className="relative mb-3">
                    <input
                      type="number"
                      min="0"
                      data-testid="admin-price-week-value"
                      value={priceWeekValue}
                      onChange={(e) => setPriceWeekValue(e.target.value)}
                      placeholder={t.admin.price_per_week}
                      className="field-input pr-9"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-maquis-soft">€</span>
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={addPricePeriod}
                disabled={!range?.from || (priceValue === "" && priceWeekValue === "")}
                data-testid="admin-add-price"
                className="btn-outline-elegant text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-40"
              >
                {t.admin.add_price}
              </button>
            </div>
            {f.blocked_ranges.length === 0 ? (
              <p data-testid="admin-no-ranges" className="text-sm text-maquis-soft">{t.admin.no_ranges}</p>
            ) : (
              <div data-testid="admin-ranges-list" className="overflow-hidden rounded-md border border-maquis-deep/10">
                <p className="border-b border-maquis-deep/10 bg-ivoire px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-maquis-soft">
                  {t.admin.ranges_summary}
                </p>
                <ul className="divide-y divide-maquis-deep/5 bg-casse">
                  {f.blocked_ranges.map((r, i) => {
                    const guest = [r.guest_first_name, r.guest_last_name].filter(Boolean).join(" ");
                    return (
                      <li
                        key={`${r.start}-${r.end}-${i}`}
                        data-testid={`admin-range-${i}`}
                        className="flex items-center justify-between gap-4 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-maquis-deep">
                            {r.start === r.end ? fmtDay(r.start) : `${fmtDay(r.start)} → ${fmtDay(r.end)}`}
                          </p>
                          {guest ? (
                            <p className="mt-0.5 text-xs text-maquis-olive">{t.admin.guest_on} : {guest}</p>
                          ) : (
                            <p className="mt-0.5 text-xs italic text-maquis-soft/70">{t.admin.no_guest_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => editRange(i)}
                            data-testid={`admin-range-edit-${i}`}
                            className="rounded-full p-1.5 text-maquis-soft transition-colors hover:bg-maquis-olive/15 hover:text-maquis-deep"
                            aria-label="Modifier la période"
                          >
                            <Pencil size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRange(i)}
                            data-testid={`admin-range-remove-${i}`}
                            className="rounded-full p-1.5 text-maquis-soft transition-colors hover:bg-[#B08D57]/15 hover:text-[#7A5A2B]"
                            aria-label="Supprimer la période"
                          >
                            <X size={14} strokeWidth={2} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 space-y-6 rounded-md border border-maquis-deep/10 bg-casse/60 p-5">
        <p className="field-label">{t.admin.pricing_title}</p>

        <div>
          <p className="mb-2 text-sm font-medium text-maquis-deep">{t.admin.price_periods}</p>
          <p className="mb-3 text-xs text-maquis-soft">{t.admin.price_period_hint}</p>
          {f.price_periods.length === 0 ? (
            <p data-testid="admin-no-prices" className="text-sm text-maquis-soft">{t.admin.no_prices}</p>
          ) : (
            <ul className="space-y-2" data-testid="admin-prices-list">
              {f.price_periods.map((r, i) => (
                <li
                  key={`${r.start}-${r.end}-${i}`}
                  data-testid={`admin-price-${i}`}
                  className="flex items-center justify-between gap-4 rounded-md border border-maquis-deep/10 bg-casse px-4 py-2.5"
                >
                  <p className="text-sm text-maquis-deep">
                    {r.start === r.end ? fmtDay(r.start) : `${fmtDay(r.start)} → ${fmtDay(r.end)}`}
                    <span className="ml-2 font-semibold">
                      {r.price} {r.per === "night" ? t.admin.price_unit_night : t.admin.price_unit_week}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => removePricePeriod(i)}
                    data-testid={`admin-price-remove-${i}`}
                    aria-label="Supprimer le tarif"
                    className="rounded-full p-1.5 text-maquis-soft transition-colors hover:bg-[#B08D57]/15 hover:text-[#7A5A2B]"
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div data-testid="admin-simulator" className="rounded-md border border-maquis-deep/10 bg-ivoire/70 p-4">
          <p className="mb-1 text-sm font-medium text-maquis-deep">{t.admin.sim_title}</p>
          <p className="mb-4 text-xs text-maquis-soft">{t.admin.sim_hint}</p>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="min-w-0">
              <label className="field-label">{t.admin.sim_arrival}</label>
              <DateInput data-testid="admin-sim-arrival" value={simArrival} onChange={(e) => setSimArrival(e.target.value)} />
            </div>
            <div className="min-w-0">
              <label className="field-label">{t.admin.sim_departure}</label>
              <DateInput data-testid="admin-sim-departure" value={simDeparture} onChange={(e) => setSimDeparture(e.target.value)} />
            </div>
            <div className="min-w-0">
              <label className="field-label">{t.admin.sim_guests}</label>
              <input type="number" min="1" data-testid="admin-sim-guests" value={simGuests} onChange={(e) => setSimGuests(e.target.value)} placeholder={t.contact.guests_placeholder} className="field-input" />
            </div>
            <div className="flex items-end min-w-0">
              <button
                type="button"
                onClick={simulate}
                data-testid="admin-sim-compute"
                className="btn-outline-elegant w-full text-maquis-deep hover:bg-maquis-deep hover:text-casse"
              >
                {t.admin.sim_compute}
              </button>
            </div>
          </div>
          {simResult && (
            simResult.error ? (
              <p data-testid="admin-sim-error" className="mt-4 text-sm text-[#7A5A2B]">{simResult.error}</p>
            ) : (
              <div data-testid="admin-sim-result" className="mt-4 overflow-hidden rounded-md border border-maquis-deep/10 bg-casse">
                <ul className="divide-y divide-maquis-deep/5 text-sm">
                  <li data-testid="admin-sim-base-row" className="flex justify-between px-4 py-2.5">
                    <span className="text-maquis-soft">{t.admin.sim_base} · {simResult.nights} {t.admin.sim_nights}</span>
                    <span className="font-medium text-maquis-deep">{simResult.base.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</span>
                  </li>
                  {simResult.discount > 0 && (
                    <li data-testid="admin-sim-discount-row" className="flex justify-between px-4 py-2.5">
                      <span className="text-maquis-soft">{t.admin.sim_discount} ({simResult.discLabel})</span>
                      <span className="font-medium text-maquis-olive">− {simResult.discount.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</span>
                    </li>
                  )}
                  {simResult.cleaning > 0 && (
                    <li data-testid="admin-sim-cleaning-row" className="flex justify-between px-4 py-2.5">
                      <span className="text-maquis-soft">{t.admin.sim_cleaning}</span>
                      <span className="font-medium text-maquis-deep">+ {simResult.cleaning.toLocaleString("fr-FR")} €</span>
                    </li>
                  )}
                  {simResult.extra > 0 && (
                    <li data-testid="admin-sim-extra-row" className="flex justify-between px-4 py-2.5">
                      <span className="text-maquis-soft">{t.admin.sim_extra} ({simResult.extraGuests})</span>
                      <span className="font-medium text-maquis-deep">+ {simResult.extra.toLocaleString("fr-FR")} €</span>
                    </li>
                  )}
                  <li className="flex justify-between bg-ivoire px-4 py-3">
                    <span className="font-medium text-maquis-deep">{t.admin.sim_total}</span>
                    <span data-testid="admin-sim-total" className="font-serif text-lg font-semibold text-maquis-deep">
                      {Math.round(simResult.total).toLocaleString("fr-FR")} €
                    </span>
                  </li>
                </ul>
              </div>
            )
          )}
        </div>

        <div className="grid gap-8 sm:grid-cols-2">
          <div>
            <p className="field-label">{t.admin.fees_title}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
                  <input type="checkbox" data-testid="admin-has-extra-fee" checked={f.has_extra_person_fee} onChange={set("has_extra_person_fee")} className="h-4 w-4 accent-[#4B5840]" />
                  {t.admin.fee_person}
                </label>
                {f.has_extra_person_fee && (
                  <input type="number" min="0" data-testid="admin-extra-person-fee" value={f.extra_person_fee} onChange={set("extra_person_fee")} placeholder="€/nuit" className="field-input w-28" />
                )}
              </div>
              <div className="flex items-center justify-between gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
                  <input type="checkbox" data-testid="admin-has-cleaning-fee" checked={f.has_cleaning_fee} onChange={set("has_cleaning_fee")} className="h-4 w-4 accent-[#4B5840]" />
                  {t.admin.fee_cleaning}
                </label>
                {f.has_cleaning_fee && (
                  <input type="number" min="0" data-testid="admin-cleaning-fee" value={f.cleaning_fee} onChange={set("cleaning_fee")} placeholder="€/séjour" className="field-input w-28" />
                )}
              </div>
            </div>
          </div>
          <div>
            <p className="field-label">{t.admin.discounts_title}</p>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
                  <input type="checkbox" data-testid="admin-has-short-discount" checked={f.has_short_discount} onChange={set("has_short_discount")} className="h-4 w-4 accent-[#4B5840]" />
                  {t.admin.disc_short}
                </label>
                {f.has_short_discount && (
                  <span className="flex items-center gap-2">
                    <input type="number" min="0" max="100" data-testid="admin-short-discount" value={f.short_stay_discount} onChange={set("short_stay_discount")} placeholder="%" className="field-input w-20" />
                    <input type="number" min="1" data-testid="admin-short-max-nights" value={f.short_stay_max_nights} onChange={set("short_stay_max_nights")} placeholder={t.admin.nights_max} className="field-input w-24" />
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-maquis-deep">
                  <input type="checkbox" data-testid="admin-has-long-discount" checked={f.has_long_discount} onChange={set("has_long_discount")} className="h-4 w-4 accent-[#4B5840]" />
                  {t.admin.disc_long}
                </label>
                {f.has_long_discount && (
                  <span className="flex items-center gap-2">
                    <input type="number" min="0" max="100" data-testid="admin-long-discount" value={f.long_stay_discount} onChange={set("long_stay_discount")} placeholder="%" className="field-input w-20" />
                    <input type="number" min="1" data-testid="admin-long-min-nights" value={f.long_stay_min_nights} onChange={set("long_stay_min_nights")} placeholder={t.admin.nights_min} className="field-input w-24" />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 max-sm:flex-col max-sm:items-start sm:gap-3">
        <button type="submit" data-testid="admin-prop-save" disabled={saving}
          className="btn-outline-elegant max-sm:!px-3 max-sm:!py-2 max-sm:!text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50">
          {saving ? "…" : t.admin.save}
        </button>
        <button type="button" data-testid="admin-prop-cancel" onClick={onCancel}
          className="btn-outline-elegant max-sm:!px-3 max-sm:!py-2 max-sm:!text-[10px] border-transparent text-maquis-soft hover:text-maquis-deep">
          {t.admin.cancel}
        </button>
        {onDelete && (
          <button type="button" data-testid="admin-prop-delete" onClick={onDelete}
            className="btn-outline-elegant max-sm:!px-3 max-sm:!py-2 max-sm:!text-[10px] ml-auto max-sm:ml-0 border-[#B08D57]/40 text-[#7A5A2B] hover:bg-[#B08D57] hover:text-casse">
            {t.admin.delete}
          </button>
        )}
      </div>
    </form>
  );
}

function StatsStayList({ t, lang, userEmail, bookings, propertyId }) {
  const minArrival = userEmail === MARGOT_EMAIL ? MARGOT_STATS_START : null;
  const stays = bookings
    .filter((b) => b.property_id === propertyId && b.status !== "REFUSEE" && (!minArrival || (b.arrival || "") >= minArrival))
    .sort((a, b) => (a.arrival || "").localeCompare(b.arrival || ""));
  if (!stays.length) {
    return <p data-testid="stats-detail-empty" className="mt-6 text-sm text-maquis-soft">{t.admin.stats_no_stay}</p>;
  }
  const byMonth = {};
  stays.forEach((b) => {
    const k = (b.arrival || "").slice(0, 7);
    (byMonth[k] = byMonth[k] || []).push(b);
  });
  const fmtMonth = (k) => {
    try {
      return format(new Date(`${k}-01T12:00:00`), "MMMM yyyy", { locale: lang === "fr" ? frLocale : undefined });
    } catch {
      return k;
    }
  };
  return (
    <div className="mt-6 space-y-6" data-testid="stats-detail-list">
      {Object.keys(byMonth).sort().map((k) => (
        <div key={k}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-maquis-soft">{fmtMonth(k)}</p>
          <ul className="mt-2 divide-y divide-maquis-deep/5">
            {byMonth[k].map((b) => (
              <li key={b.id} data-testid={`stats-stay-${b.id}`} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                <span className="font-medium text-maquis-deep">{b.first_name} {b.last_name}</span>
                <span className="text-maquis-olive">{b.arrival} → {b.departure}</span>
                <span className="font-semibold text-maquis-deep">{(b.stay_total || 0).toLocaleString("fr-FR")} €</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function StatsTab({ t, bookings = [] }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [detailProp, setDetailProp] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!stats) {
    return <p data-testid="stats-loading" className="mt-10 text-sm text-maquis-soft">…</p>;
  }
  const cards = [
    ["stats_booking_rate", `${stats.booking_rate} %`, `${stats.bookings_confirmed} / ${stats.bookings_total}`],
    ["stats_sejours", stats.sejours_effectues, null],
    ["stats_ca_week", `${stats.ca_week.toLocaleString("fr-FR")} €`, null],
    ["stats_ca_month", `${stats.ca_month.toLocaleString("fr-FR")} €`, null],
    ["stats_ca_year", `${stats.ca_year.toLocaleString("fr-FR")} €`, null],
    ["stats_fill", `${stats.fill_rate_global} %`, null],
    ["stats_airbnb", stats.airbnb_clicks_total, null],
    ["stats_reviews", stats.reviews_count, stats.reviews_avg ? `${stats.reviews_avg}/5` : null],
    ["stats_pending", stats.bookings_pending, null],
  ];
  const statusData = STATUS_KEYS
    .map((s) => ({ key: s, name: t.status[s], value: (stats.bookings_by_status || {})[s] || 0 }))
    .filter((d) => d.value > 0);
  return (
    <section className="mt-10" data-testid="admin-stats-section">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map(([key, value, sub]) => (
          <div key={key} data-testid={`stat-card-${key}`} className="rounded-md border border-maquis-deep/10 bg-casse p-5">
            <p className="text-[11px] uppercase tracking-[0.15em] text-maquis-soft">{t.admin[key]}</p>
            <p className="mt-2 font-serif text-3xl text-maquis-deep">{value}</p>
            {sub != null && <p className="mt-1 text-xs text-maquis-soft">{sub}</p>}
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs italic text-maquis-soft">{t.admin.stats_ca_hint}</p>

      {(user?.email === CARO_EMAIL || user?.email === MARGOT_EMAIL) && (
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {user.email === CARO_EMAIL && (
            <div data-testid="stats-commission-caro" className="rounded-md border border-maquis-deep/10 bg-ivoire p-5">
              <p className="eyebrow">{t.admin.stats_commission_caro}</p>
              <p className="mt-2 font-serif text-3xl text-maquis-deep">{(stats.commission_caro || 0).toLocaleString("fr-FR")} €</p>
            </div>
          )}
          {user.email === MARGOT_EMAIL && stats.commission_margot != null && (
            <>
              <div data-testid="stats-commission-margot" className="rounded-md border border-maquis-deep/10 bg-ivoire p-5">
                <p className="eyebrow">{t.admin.stats_commission_margot}</p>
                <p className="mt-2 font-serif text-3xl text-maquis-deep">{stats.commission_margot.toLocaleString("fr-FR")} €</p>
              </div>
              <div data-testid="stats-margot-received" className="rounded-md border border-maquis-deep/10 bg-ivoire p-5">
                <p className="eyebrow">{t.admin.margot_received}</p>
                <p className="mt-2 font-serif text-3xl text-maquis-olive">{(stats.commission_margot_paid || 0).toLocaleString("fr-FR")} €</p>
              </div>
              <div data-testid="stats-margot-due" className="rounded-md border border-maquis-deep/10 bg-ivoire p-5">
                <p className="eyebrow">{t.admin.margot_due}</p>
                <p className="mt-2 font-serif text-3xl text-[#7A5A2B]">{((stats.commission_margot || 0) - (stats.commission_margot_paid || 0)).toLocaleString("fr-FR")} €</p>
              </div>
            </>
          )}
        </div>
      )}

      <h3 className="mt-10 font-serif text-2xl text-maquis-deep">{t.admin.stats_monthly}</h3>
      {stats.ca_by_month.length === 0 ? (
        <p data-testid="stats-no-price" className="mt-3 text-sm text-maquis-soft">{t.admin.stats_no_price}</p>
      ) : (
        <div className="mt-4 h-72 rounded-md border border-maquis-deep/10 bg-casse p-4" data-testid="stats-chart-monthly">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.ca_by_month} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#CFC5B1" opacity={0.5} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#717F63" }} stroke="#CFC5B1" />
              <YAxis tick={{ fontSize: 12, fill: "#717F63" }} stroke="#CFC5B1" width={70} />
              <Tooltip formatter={(v) => [`${Number(v).toLocaleString("fr-FR")} €`, t.admin.stats_th_ca]} />
              <Area type="monotone" dataKey="amount" stroke="#4B5840" fill="#4B5840" fillOpacity={0.16} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div>
          <h3 className="font-serif text-2xl text-maquis-deep">{t.admin.stats_status_chart}</h3>
          <div className="mt-4 h-72 rounded-md border border-maquis-deep/10 bg-casse p-4" data-testid="stats-chart-status">
            {statusData.length === 0 ? (
              <p className="pt-24 text-center text-sm text-maquis-soft">{t.admin.no_bookings}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" strokeWidth={2}>
                    {statusData.map((d) => (
                      <Cell key={d.key} fill={STATUS_COLORS[d.key]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div>
          <h3 className="font-serif text-2xl text-maquis-deep">{t.admin.stats_fill_chart}</h3>
          <div className="mt-4 h-72 rounded-md border border-maquis-deep/10 bg-casse p-4" data-testid="stats-chart-fill">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.per_property} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CFC5B1" opacity={0.5} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#717F63" }} stroke="#CFC5B1" />
                <YAxis tick={{ fontSize: 12, fill: "#717F63" }} stroke="#CFC5B1" unit=" %" />
                <Tooltip formatter={(v) => [`${v} %`, t.admin.stats_fill]} />
                <Bar dataKey="fill_rate" fill="#4B5840" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <h3 className="mt-10 font-serif text-2xl text-maquis-deep">{t.admin.stats_per_property}</h3>
      <div className="mt-4 overflow-x-auto rounded-md border border-maquis-deep/10 bg-casse">
        <table className="w-full min-w-[900px] text-sm" data-testid="stats-table">
          <thead>
            <tr className="border-b border-maquis-deep/10 text-left text-xs uppercase tracking-[0.12em] text-maquis-soft">
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_property}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_airbnb}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_bookings}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_sejours}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_ca}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_occ}</th>
              <th className="px-5 py-3 font-medium">{t.admin.stats_th_fill}</th>
            </tr>
          </thead>
          <tbody>
            {stats.per_property.map((p) => (
              <tr key={p.id} data-testid={`stats-row-${p.slug}`} className="border-b border-maquis-deep/5 last:border-0">
                <td className="px-5 py-3 font-medium text-maquis-deep">
                  <button
                    type="button"
                    data-testid={`stats-prop-link-${p.slug}`}
                    onClick={() => setDetailProp(p)}
                    className="underline decoration-maquis-deep/30 underline-offset-4 transition-colors hover:text-maquis-olive"
                  >
                    {p.name}
                  </button>
                </td>
                <td className="px-5 py-3">{p.airbnb_clicks}</td>
                <td className="px-5 py-3">{p.bookings}</td>
                <td className="px-5 py-3">{p.sejours}</td>
                <td className="px-5 py-3">{p.revenue.toLocaleString("fr-FR")} €</td>
                <td className="px-5 py-3">{p.occ_days}</td>
                <td className="px-5 py-3">{p.fill_rate} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailProp && (
        <div
          data-testid="stats-detail-overlay"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-mer/70 p-4 backdrop-blur-sm"
          onClick={() => setDetailProp(null)}
        >
          <div
            data-testid="stats-detail-modal"
            className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-md bg-casse p-8 shadow-[0_30px_80px_-30px_rgba(42,51,35,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">{t.admin.stats_stays_by_month}</p>
                <h3 className="mt-2 font-serif text-2xl text-maquis-deep">{detailProp.name}</h3>
              </div>
              <button
                data-testid="stats-detail-close"
                onClick={() => setDetailProp(null)}
                aria-label="Fermer"
                className="rounded-full p-2 text-maquis-soft transition-colors hover:bg-ivoire hover:text-maquis-deep"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </div>
            <StatsStayList t={t} lang={lang} userEmail={user?.email} bookings={bookings} propertyId={detailProp.id} />
          </div>
        </div>
      )}
    </section>
  );
}

function AssignProperty({ t, booking, properties, onAssign }) {
  const [pid, setPid] = useState(booking.property_id || "");
  return (
    <div className="mt-6 rounded-md border border-maquis-deep/10 bg-ivoire/60 p-4" data-testid="booking-assign-property">
      <p className="field-label">{t.admin.assign_property}</p>
      <div className="mt-3 flex items-center gap-3">
        <select
          data-testid="assign-property-select"
          value={pid}
          onChange={(e) => setPid(e.target.value)}
          className="field-input flex-1"
        >
          <option value="">{t.admin.assign_property_placeholder}</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          data-testid="assign-property-save"
          disabled={!pid || pid === (booking.property_id || "")}
          onClick={() => onAssign(booking.id, pid)}
          className="btn-outline-elegant !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t.admin.site_save}
        </button>
      </div>
    </div>
  );
}


function ApiTab({ t, properties }) {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [sending, setSending] = useState(false);
  const [extra, setExtra] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const unlock = async () => {
    try {
      await api.post("/seo/verify", { code });
      const { data } = await api.get("/admin/email-config", { headers: { "x-seo-code": code } });
      setCfg(data);
      setUnlocked(true);
    } catch (e) {
      toast.error(t.admin.seo_wrong_code);
    }
  };

  if (!unlocked) {
    return (
      <div className="mx-auto mt-10 max-w-md rounded-md border border-maquis-deep/10 bg-casse p-8 text-center" data-testid="api-lock">
        <p className="text-sm text-maquis-soft">{t.admin.seo_locked_hint}</p>
        <input
          type="password"
          inputMode="numeric"
          data-testid="api-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && unlock()}
          placeholder={t.admin.seo_code_label}
          className="field-input mt-4 text-center"
        />
        <button data-testid="api-unlock-btn" onClick={unlock} className="btn-outline-elegant mt-4 text-maquis-deep hover:bg-maquis-deep hover:text-casse">
          {t.admin.seo_unlock}
        </button>
      </div>
    );
  }
  if (!cfg) return null;

  const setField = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const selected = cfg.photos || [];
  const selIdx = (url) => selected.findIndex((p) => p.url === url);
  const togglePhoto = (url, slug) => {
    const i = selIdx(url);
    if (i >= 0) setCfg((s) => ({ ...s, photos: s.photos.filter((p) => p.url !== url) }));
    else if (selected.length < 4) setCfg((s) => ({ ...s, photos: [...(s.photos || []), { url, slug }] }));
  };

  const movePhoto = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= selected.length) return;
    const arr = [...selected];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCfg((s) => ({ ...s, photos: arr }));
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setExtra((s) => (s.some((p) => p.url === data.url) ? s : [...s, { url: data.url, slug: "", name: file.name }]));
        setCfg((s) => ((s.photos || []).length < 4 ? { ...s, photos: [...(s.photos || []), { url: data.url, slug: "" }] } : s));
      }
      toast.success(t.admin.updated);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhotos(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    try {
      await api.put("/admin/email-config", cfg, { headers: { "x-seo-code": code } });
      toast.success(t.admin.api_saved);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/admin/email-config/test", {}, { headers: { "x-seo-code": code } });
      toast.success(t.admin.api_test_sent);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const seen = new Set(extra.map((p) => p.url));
  const photoPool = [...extra];
  properties.forEach((p) => {
    [p.cover_image, ...(p.images || [])].filter(Boolean).forEach((u) => {
      if (!seen.has(u)) {
        seen.add(u);
        photoPool.push({ url: u, slug: p.slug, name: p.name });
      }
    });
  });

  return (
    <div className="mt-10" data-testid="api-tab">
      <section className="rounded-md border border-maquis-deep/10 bg-casse p-5">
        <h2 className="eyebrow">{t.admin.api_email_title}</h2>
        <div className="mt-5 grid gap-4">
          <div>
            <label className="field-label">{t.admin.api_subject}</label>
            <input data-testid="api-subject" value={cfg.subject || ""} onChange={(e) => setField("subject", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label">{t.admin.api_heading}</label>
            <input data-testid="api-heading" value={cfg.heading || ""} onChange={(e) => setField("heading", e.target.value)} className="field-input" />
          </div>
          <div>
            <label className="field-label">{t.admin.api_body}</label>
            <textarea data-testid="api-body" rows={9} value={cfg.body || ""} onChange={(e) => setField("body", e.target.value)} className="field-input resize-y" />
            <p className="mt-2 text-xs text-maquis-soft">{t.admin.api_body_hint}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label">{t.admin.api_cta_label}</label>
              <input data-testid="api-cta-label" value={cfg.cta_label || ""} onChange={(e) => setField("cta_label", e.target.value)} className="field-input" />
            </div>
            <div>
              <label className="field-label">{t.admin.api_cta_url}</label>
              <input data-testid="api-cta-url" value={cfg.cta_url || ""} onChange={(e) => setField("cta_url", e.target.value)} className="field-input" />
            </div>
            <div>
              <label className="field-label">{t.admin.api_ig_label}</label>
              <input data-testid="api-ig-label" value={cfg.instagram_label || ""} onChange={(e) => setField("instagram_label", e.target.value)} className="field-input" />
            </div>
            <div>
              <label className="field-label">{t.admin.api_ig_url}</label>
              <input data-testid="api-ig-url" value={cfg.instagram_url || ""} onChange={(e) => setField("instagram_url", e.target.value)} className="field-input" />
            </div>
          </div>
          <div>
            <label className="field-label">{t.admin.api_photos}</label>
            {selected.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-3">
                {selected.map((p, i) => (
                  <div key={p.url} className="flex flex-col items-center gap-1.5" data-testid={`api-photo-slot-${i}`}>
                    <div className="relative">
                      <img src={p.url} alt="" className="h-20 w-20 rounded-sm object-cover" />
                      <span className="absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-maquis-deep text-[10px] text-casse">{i + 1}</span>
                      <button type="button" data-testid={`api-photo-remove-${i}`} onClick={() => togglePhoto(p.url, p.slug)} aria-label="Retirer" className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-casse text-maquis-deep shadow">
                        <Trash2 size={10} strokeWidth={1.75} />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" data-testid={`api-photo-left-${i}`} onClick={() => movePhoto(i, -1)} disabled={i === 0} aria-label="Déplacer à gauche" className="flex h-6 w-6 items-center justify-center rounded-full border border-maquis-deep/25 text-maquis-deep transition-colors duration-200 hover:border-maquis-deep disabled:opacity-30">
                        <ChevronLeft size={12} strokeWidth={1.75} />
                      </button>
                      <button type="button" data-testid={`api-photo-right-${i}`} onClick={() => movePhoto(i, 1)} disabled={i === selected.length - 1} aria-label="Déplacer à droite" className="flex h-6 w-6 items-center justify-center rounded-full border border-maquis-deep/25 text-maquis-deep transition-colors duration-200 hover:border-maquis-deep disabled:opacity-30">
                        <ChevronRight size={12} strokeWidth={1.75} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mb-3 text-xs text-maquis-soft">{t.admin.api_photos_hint}</p>
            <label data-testid="api-photo-upload-label" className="btn-outline-elegant mb-3 inline-flex cursor-pointer items-center gap-2 !px-4 !py-2 text-[11px] uppercase tracking-[0.15em] text-maquis-deep hover:bg-maquis-deep hover:text-casse">
              <Upload size={13} strokeWidth={1.75} /> {uploadingPhotos ? t.admin.uploading : t.admin.api_upload}
              <input type="file" accept="image/*" multiple data-testid="api-photo-upload" className="hidden" onChange={uploadPhotos} />
            </label>
            <div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-8" data-testid="api-photo-pool">
              {photoPool.map((p, i) => (
                <button
                  type="button"
                  key={p.url}
                  data-testid={`api-photo-pick-${i}`}
                  onClick={() => togglePhoto(p.url, p.slug)}
                  className={`relative overflow-hidden rounded-sm border-2 transition-colors ${selIdx(p.url) >= 0 ? "border-maquis-deep" : "border-transparent hover:border-maquis-deep/40"}`}
                >
                  <img src={p.url} alt={p.name} loading="lazy" className="h-16 w-full object-cover" />
                  {selIdx(p.url) >= 0 && (
                    <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-maquis-deep text-[9px] text-casse">{selIdx(p.url) + 1}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="api-save" onClick={save} className="btn-outline-elegant text-maquis-deep hover:bg-maquis-deep hover:text-casse">
            {t.admin.api_save}
          </button>
          <button data-testid="api-test-send" onClick={sendTest} disabled={sending} className="btn-outline-elegant text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50">
            {sending ? "…" : t.admin.api_test}
          </button>
        </div>
      </section>
    </div>
  );
}

function SeoTab({ t, properties }) {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [pages, setPages] = useState(null);
  const [propSeo, setPropSeo] = useState({});
  const [newCode, setNewCode] = useState("");

  const unlock = async () => {
    try {
      await api.post("/seo/verify", { code });
      const { data } = await api.get("/seo/public");
      setPages(data.pages || {});
      const ps = {};
      properties.forEach((p) => { ps[p.id] = { slug: p.slug || "", seo_title: p.seo_title || "", seo_description: p.seo_description || "" }; });
      setPropSeo(ps);
      setUnlocked(true);
    } catch (e) {
      toast.error(t.admin.seo_wrong_code);
    }
  };

  const setPage = (key, field, value) => setPages((s) => ({ ...s, [key]: { ...s[key], [field]: value } }));
  const setProp = (id, field, value) => setPropSeo((s) => ({ ...s, [id]: { ...s[id], [field]: value } }));

  const savePages = async () => {
    try {
      const payload = {};
      Object.entries(pages).forEach(([k, p]) => { payload[k] = { title: p.title || "", description: p.description || "", slug: p.slug || "" }; });
      await api.put("/admin/seo", { pages: payload }, { headers: { "x-seo-code": code } });
      toast.success(t.admin.seo_saved);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePropSeo = async (p) => {
    try {
      const { data } = await api.put(`/admin/properties/${p.id}/seo`, propSeo[p.id], { headers: { "x-seo-code": code } });
      setProp((s) => ({ ...s, [p.id]: { ...s[p.id], slug: data.slug || s[p.id].slug } }));
      toast.success(t.admin.seo_saved);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeCode = async () => {
    try {
      await api.post("/admin/seo/code", { new_code: newCode }, { headers: { "x-seo-code": code } });
      setCode(newCode);
      setNewCode("");
      toast.success(t.admin.seo_code_changed);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!unlocked) {
    return (
      <div className="mx-auto mt-10 max-w-md rounded-md border border-maquis-deep/10 bg-casse p-8 text-center" data-testid="seo-lock">
        <p className="text-sm text-maquis-soft">{t.admin.seo_locked_hint}</p>
        <input
          type="password"
          inputMode="numeric"
          data-testid="seo-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && unlock()}
          placeholder={t.admin.seo_code_label}
          className="field-input mt-4 text-center"
        />
        <button data-testid="seo-unlock-btn" onClick={unlock} className="btn-outline-elegant mt-4 text-maquis-deep hover:bg-maquis-deep hover:text-casse">
          {t.admin.seo_unlock}
        </button>
      </div>
    );
  }
  if (!pages) return null;
  return (
    <div className="mt-10 space-y-12" data-testid="seo-tab">
      <section>
        <h2 className="eyebrow">{t.admin.seo_pages_title}</h2>
        <div className="mt-5 space-y-6">
          {Object.entries(pages).map(([key, p]) => (
            <div key={key} className="rounded-md border border-maquis-deep/10 bg-casse p-5" data-testid={`seo-page-${key}`}>
              <p className="font-serif text-lg text-maquis-deep">
                {p.label} <span className="text-xs text-maquis-soft">({p.slug ? `/${p.slug}` : p.path})</span>
              </p>
              <div className="mt-3 grid gap-3">
                <input data-testid={`seo-title-${key}`} value={p.title || ""} onChange={(e) => setPage(key, "title", e.target.value)} placeholder={t.admin.seo_page_title} className="field-input" />
                <textarea data-testid={`seo-desc-${key}`} value={p.description || ""} onChange={(e) => setPage(key, "description", e.target.value)} placeholder={t.admin.seo_page_desc} rows={2} className="field-input" />
                <input data-testid={`seo-slug-${key}`} value={p.slug || ""} onChange={(e) => setPage(key, "slug", e.target.value)} placeholder={t.admin.seo_page_slug} className="field-input" />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-maquis-soft">{t.admin.seo_slug_hint}</p>
        <button data-testid="seo-save-pages" onClick={savePages} className="btn-outline-elegant mt-4 text-maquis-deep hover:bg-maquis-deep hover:text-casse">
          {t.admin.seo_save}
        </button>
      </section>
      <section>
        <h2 className="eyebrow">{t.admin.seo_properties_title}</h2>
        <div className="mt-5 space-y-6">
          {properties.map((p) => (
            <div key={p.id} className="rounded-md border border-maquis-deep/10 bg-casse p-5" data-testid={`seo-prop-${p.slug}`}>
              <p className="font-serif text-lg text-maquis-deep">
                {p.name} <span className="text-xs text-maquis-soft">(/locations/{propSeo[p.id]?.slug || p.slug})</span>
              </p>
              <div className="mt-3 grid gap-3">
                <input data-testid={`seo-prop-slug-${p.slug}`} value={propSeo[p.id]?.slug || ""} onChange={(e) => setProp(p.id, "slug", e.target.value)} placeholder={t.admin.seo_page_slug} className="field-input" />
                <input data-testid={`seo-prop-title-${p.slug}`} value={propSeo[p.id]?.seo_title || ""} onChange={(e) => setProp(p.id, "seo_title", e.target.value)} placeholder={t.admin.seo_page_title} className="field-input" />
                <textarea data-testid={`seo-prop-desc-${p.slug}`} value={propSeo[p.id]?.seo_description || ""} onChange={(e) => setProp(p.id, "seo_description", e.target.value)} placeholder={t.admin.seo_page_desc} rows={2} className="field-input" />
              </div>
              <button data-testid={`seo-prop-save-${p.slug}`} onClick={() => savePropSeo(p)} className="btn-outline-elegant mt-3 !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse">
                {t.admin.site_save}
              </button>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="eyebrow">{t.admin.seo_code_change}</h2>
        <div className="mt-3 flex max-w-sm items-center gap-3">
          <input type="password" data-testid="seo-new-code" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder={t.admin.seo_code_new} className="field-input" />
          <button data-testid="seo-change-code" onClick={changeCode} className="btn-outline-elegant !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse">
            {t.admin.site_save}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function AdminPage() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "bookings");
  const [group, setGroup] = useState("admin");
  const [bookings, setBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selDemandes, setSelDemandes] = useState([]);
  const [selMessages, setSelMessages] = useState([]);
  const [commissionInput, setCommissionInput] = useState("");
  const [margotInput, setMargotInput] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    return () => {
      const p = window.location.pathname;
      if (!p.startsWith("/admin") && !p.startsWith("/espace-proprietaire")) logout();
    };
  }, [logout]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null); // property id | "new" | null

  const isAdmin = user && user.role === "admin";

  const loadAll = () => {
    if (!isAdmin) return;
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/properties").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/admin/owners").then((r) => setOwners(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(loadAll, [isAdmin]);

  const demandes = [
    ...bookings.map((b) => ({ ...b, _kind: "booking" })),
    ...contacts
      .filter((c) => c.kind === "location" || c.kind === "mixte")
      .map((c) => ({ ...c, _kind: "contact", services: c.services || [], providers: c.providers || [] })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  if (user === null) {
    return <main className="flex min-h-screen items-center justify-center pt-20 text-maquis-soft">{t.auth.checking}</main>;
  }

  if (!isAdmin) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen px-5 pb-24 pt-32 sm:pt-40">
        <LoginForm />
      </main>
    );
  }

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(t.admin.status_updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success(t.admin.status_updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const assignProperty = async (id, propertyId) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { property_id: propertyId });
      const prop = properties.find((p) => p.id === propertyId);
      const patch = { property_id: propertyId, property_name: prop?.name || "" };
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
      setSelectedBooking((s) => (s && s.id === id ? { ...s, ...patch } : s));
      toast.success(t.admin.assign_property_done);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveProperty = async (payload) => {
    try {
      if (editing === "new") {
        await api.post("/admin/properties", payload);
        toast.success(t.admin.created);
      } else {
        await api.put(`/admin/properties/${editing}`, payload);
        toast.success(t.admin.updated);
      }
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleHidden = async (p) => {
    try {
      await api.patch(`/admin/properties/${p.id}/hidden`, { hidden: !p.hidden });
      toast.success(t.admin.updated);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm(t.admin.delete_confirm)) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      toast.success(t.admin.updated);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const requireDeleteCode = () => {
    const code = window.prompt(t.admin.delete_code_prompt);
    if (code === null) return false;
    if (code !== "190412") {
      toast.error(t.admin.delete_code_wrong);
      return false;
    }
    return true;
  };

  const deleteDemande = async (item) => {
    if (item._kind !== "contact" && !requireDeleteCode()) return;
    if (!window.confirm(t.admin.demande_delete_confirm)) return;
    try {
      if (item._kind === "contact") {
        await api.delete(`/admin/contacts/${item.id}`);
        setContacts((cs) => cs.filter((c) => c.id !== item.id));
      } else {
        await api.delete(`/admin/bookings/${item.id}`);
        setBookings((bs) => bs.filter((b) => b.id !== item.id));
      }
      if (selectedBooking?.id === item.id) setSelectedBooking(null);
      toast.success(t.admin.deleted);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm(t.admin.message_delete_confirm)) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast.success(t.admin.deleted);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSel = (setList, item) =>
    setList((ls) => (ls.some((i) => i.id === item.id && i._kind === item._kind)
      ? ls.filter((i) => !(i.id === item.id && i._kind === item._kind))
      : [...ls, item]));

  const bulkDelete = async (items, clear) => {
    if (items.some((i) => i._kind !== "contact") && !requireDeleteCode()) return;
    if (!window.confirm(t.admin.bulk_delete_confirm.replace("{n}", items.length))) return;
    try {
      for (const it of items) {
        await api.delete(it._kind === "contact" ? `/admin/contacts/${it.id}` : `/admin/bookings/${it.id}`);
      }
      const bIds = items.filter((i) => i._kind !== "contact").map((i) => i.id);
      const cIds = items.filter((i) => i._kind === "contact").map((i) => i.id);
      setBookings((bs) => bs.filter((b) => !bIds.includes(b.id)));
      setContacts((cs) => cs.filter((c) => !cIds.includes(c.id)));
      if (selectedBooking && items.some((i) => i.id === selectedBooking.id)) setSelectedBooking(null);
      clear();
      toast.success(t.admin.deleted);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const tabs = [
    ["bookings", t.admin.tab_bookings],
    ["properties", t.admin.tab_properties],
    ["owners", t.admin.tab_owners],
    ["contacts", t.admin.tab_contacts],
    ["reviews", t.admin.tab_reviews],
    ["stats", t.admin.tab_stats],
  ];

  const stayBadge = (b) => {
    const today = new Date().toISOString().slice(0, 10);
    if (b.departure && b.departure < today) return { label: t.admin.stay_done, cls: "bg-maquis-soft/15 text-maquis-soft border-maquis-soft/30" };
    if (b.arrival && b.arrival <= today) return { label: t.admin.stay_ongoing, cls: "bg-maquis-olive/10 text-maquis-olive border-maquis-olive/40" };
    return { label: t.admin.stay_upcoming, cls: "bg-sable/40 text-maquis-deep border-sable" };
  };

  const saveCommission = async (id, payload) => {
    try {
      await api.patch(`/admin/bookings/${id}/commission`, payload);
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
      const fresh = data.find((x) => x.id === id);
      if (fresh) setSelectedBooking(fresh);
      toast.success(t.admin.updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveClient = async () => {
    if (!editingClient) return;
    try {
      if (editingClient.kind === "guest") {
        await api.patch(`/admin/guests/${encodeURIComponent(editingClient.oldEmail)}`, {
          first_name: editingClient.first_name.trim(),
          last_name: editingClient.last_name.trim(),
          email: editingClient.email.trim(),
          phone: editingClient.phone.trim(),
        });
        setBookings((bs) => bs.map((b) => ((b.email || "").toLowerCase() === editingClient.oldEmail.toLowerCase()
          ? { ...b, first_name: editingClient.first_name.trim(), last_name: editingClient.last_name.trim(), email: editingClient.email.trim().toLowerCase(), phone: editingClient.phone.trim() }
          : b)));
      } else {
        await api.patch(`/admin/owners/${editingClient.id}`, {
          name: editingClient.name.trim(),
          email: editingClient.email.trim(),
          phone: (editingClient.phone || "").trim(),
        });
        setOwners((os) => os.map((o) => (o.id === editingClient.id ? { ...o, name: editingClient.name.trim(), email: editingClient.email.trim().toLowerCase(), phone: editingClient.phone.trim() } : o)));
      }
      toast.success(t.admin.client_saved);
      setEditingClient(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteClient = async (kind, idOrEmail) => {
    if (!window.confirm(t.admin.client_delete_confirm)) return;
    try {
      if (kind === "owner") {
        await api.delete(`/admin/owners/${idOrEmail}`);
      } else {
        await api.delete(`/admin/guests/${encodeURIComponent(idOrEmail)}`);
      }
      toast.success(t.admin.updated);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendReviewRequest = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/review-request`);
      toast.success(t.admin.review_request_sent);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success(t.admin.updated);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-ivoire px-5 pb-24 pt-28 sm:px-8 sm:pt-36">
      <div className="mx-auto max-w-6xl">
        <Link
          to="/espace-proprietaire"
          data-testid="admin-back-owner-space"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-maquis-soft transition-colors hover:text-maquis-deep"
        >
          <ArrowLeft size={14} strokeWidth={1.75} /> {t.admin.back_owner_space}
        </Link>
        <div className="mt-3">
          <h1 className="font-serif text-3xl sm:text-4xl text-maquis-deep">{t.nav.admin}</h1>
        </div>

        <div className="mt-10 grid gap-2 sm:grid-cols-2" data-testid="admin-groups">
          {[
            ["admin", t.admin.group_admin],
            ["site", t.admin.group_site],
            ["seo", t.admin.tab_seo],
            ["api", t.admin.tab_api],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-group-${key}`}
              onClick={() => setGroup(key)}
              className={`rounded-md border px-5 py-4 text-center font-serif text-xl transition-colors duration-200 ${
                group === key
                  ? "border-maquis-deep bg-maquis-deep text-casse"
                  : "border-maquis-deep/20 bg-casse text-maquis-deep hover:border-maquis-deep"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {group === "site" && <SiteContentTab t={t} />}

        {group === "seo" && <SeoTab t={t} properties={properties} />}

        {group === "api" && <ApiTab t={t} properties={properties} />}

        {group === "admin" && (
        <>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                tab === key
                  ? "border-maquis-deep bg-maquis-deep text-casse"
                  : "border-maquis-deep/25 text-maquis-deep/70 hover:border-maquis-deep"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Demandes */}
        {tab === "bookings" && (
          <section className="mt-10" data-testid="admin-bookings-section">
            <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-booking-filters">
              {[
                ["all", t.admin.filter_all],
                ["EN_ATTENTE", t.admin.filter_pending],
                ["VALIDEE", t.admin.filter_accepted],
                ["REFUSEE", t.admin.filter_refused],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`admin-filter-${key.toLowerCase().replace("_", "-")}`}
                  onClick={() => setBookingFilter(key)}
                  className={`rounded-full border px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                    bookingFilter === key
                      ? "border-maquis-deep bg-maquis-deep text-casse"
                      : "border-maquis-deep/25 text-maquis-deep/70 hover:border-maquis-deep"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {selDemandes.length > 0 && (
              <div data-testid="admin-demandes-bulkbar" className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-maquis-deep/15 bg-ivoire px-5 py-3">
                <span data-testid="admin-demandes-selected-count" className="text-xs uppercase tracking-[0.12em] text-maquis-deep">
                  {selDemandes.length} {t.admin.selected_count}
                </span>
                <button
                  data-testid="admin-demandes-bulk-delete"
                  onClick={() => bulkDelete(selDemandes, () => setSelDemandes([]))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-900/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-red-900/80 transition-colors duration-200 hover:bg-red-900 hover:text-casse"
                >
                  <Trash2 size={12} strokeWidth={1.75} /> {t.admin.delete_selected}
                </button>
                <button
                  data-testid="admin-demandes-bulk-clear"
                  onClick={() => setSelDemandes([])}
                  className="border-b border-maquis-deep/30 pb-0.5 text-xs text-maquis-deep transition-colors duration-200 hover:border-maquis-deep"
                >
                  {t.admin.clear_selection}
                </button>
              </div>
            )}
            {demandes.length === 0 ? (
              <p data-testid="admin-no-bookings" className="text-sm text-maquis-soft">{t.admin.no_bookings}</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="admin-bookings-grid">
                {demandes.filter((b) => bookingFilter === "all" || b.status === bookingFilter).map((b) => {
                  const stay = stayBadge(b);
                  return (
                    <div
                      key={`${b._kind}-${b.id}`}
                      data-testid={`admin-booking-${b.id}`}
                      onClick={() => {
                        setSelectedBooking(b);
                        setCommissionInput(b.commission_pct ?? "");
                        setMargotInput(b.margot_pct ?? "");
                      }}
                      className="cursor-pointer rounded-md border border-maquis-deep/10 bg-casse p-6 transition-colors hover:border-maquis-deep/25"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-serif text-lg text-maquis-deep">{b.first_name} {b.last_name}</p>
                          <p className="mt-0.5 text-xs text-maquis-soft">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <StatusBadge status={b.status} />
                          <button
                            data-testid={`admin-booking-select-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); toggleSel(setSelDemandes, b); }}
                            aria-label="Sélectionner"
                            aria-pressed={selDemandes.some((i) => i.id === b.id && i._kind === b._kind)}
                            className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-200 ${selDemandes.some((i) => i.id === b.id && i._kind === b._kind) ? "border-maquis-deep bg-maquis-deep text-casse" : "border-maquis-deep/40 text-transparent hover:border-maquis-deep"}`}
                          >
                            <Check size={11} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-maquis-olive">
                        {[b.property_name || null, b.arrival && b.departure ? `${b.arrival} → ${b.departure}` : null, b.guests ? `${b.guests} ${t.owner.th_guests.toLowerCase()}` : null].filter(Boolean).join(" · ") || "—"}
                      </p>
                      {[...b.services, ...b.providers].length > 0 && (
                        <p className="mt-1 text-xs text-maquis-soft">{[...b.services, ...b.providers].join(", ")}</p>
                      )}
                      {b.message && (
                        <p data-testid={`admin-booking-message-${b.id}`} className="mt-2 line-clamp-2 text-sm italic text-maquis-olive">« {b.message} »</p>
                      )}
                      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-maquis-deep/10 pt-4" onClick={(e) => e.stopPropagation()}>
                        <span data-testid={`admin-stay-status-${b.id}`} className={`rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wide ${stay.cls}`}>
                          {stay.label}
                        </span>
                        <select
                          data-testid={`admin-status-select-${b.id}`}
                          value={b.status === "VALIDEE" || b.status === "REFUSEE" ? b.status : ""}
                          onChange={(e) => e.target.value && (b._kind === "contact" ? updateContactStatus(b.id, e.target.value) : updateStatus(b.id, e.target.value))}
                          className="rounded-full border border-maquis-deep/25 bg-casse px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
                        >
                          <option value="" disabled>{t.owner.status_placeholder}</option>
                          <option value="VALIDEE">{t.owner.accept}</option>
                          <option value="REFUSEE">{t.owner.refuse}</option>
                        </select>
                        {b._kind === "booking" && (
                          b.review_request_sent_at ? (
                            <span data-testid={`admin-review-sent-${b.id}`} className="text-xs text-maquis-olive">
                              {t.admin.review_sent_label}
                            </span>
                          ) : ["VALIDEE", "EN_COURS", "TERMINEE"].includes(b.status) ? (
                            <button
                              data-testid={`admin-review-btn-${b.id}`}
                              onClick={() => sendReviewRequest(b.id)}
                              className="border-b border-maquis-deep/30 pb-0.5 text-xs text-maquis-deep transition-colors hover:border-maquis-deep"
                            >
                              {t.admin.send_review}
                            </button>
                          ) : null
                        )}
                        <button
                          data-testid={`admin-booking-delete-${b.id}`}
                          onClick={() => deleteDemande(b)}
                          aria-label={t.admin.delete}
                          className="ml-auto inline-flex items-center text-maquis-soft transition-colors duration-200 hover:text-red-900"
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Biens */}
        {tab === "properties" && (
          <section className="mt-10 space-y-6" data-testid="admin-properties-section">
            <button
              data-testid="admin-new-property-button"
              onClick={() => setEditing(editing === "new" ? null : "new")}
              className="btn-outline-elegant text-maquis-deep hover:bg-maquis-deep hover:text-casse"
            >
              {t.admin.new_property}
            </button>
            {editing === "new" && (
              <PropertyForm initial={EMPTY_PROP} onSave={saveProperty} onCancel={() => setEditing(null)} propertyId={null} onBookingsChanged={() => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {})} t={t} />
            )}
            {properties.map((p) => (
              <div key={p.id} data-testid={`admin-property-${p.slug}`} className="rounded-md border border-maquis-deep/10 bg-casse p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {(p.cover_image || p.images[0]) && <img src={p.cover_image || p.images[0]} alt={p.name} data-testid={`admin-cover-img-${p.slug}`} className="h-20 w-28 rounded-sm object-cover brightness-[1.08]" />}
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-serif text-xl text-maquis-deep">{p.name}</h3>
                        {p.hidden && (
                          <span data-testid={`admin-hidden-badge-${p.slug}`} className="rounded-full bg-[#B08D57]/15 px-3 py-1 text-[10px] uppercase tracking-[0.12em] text-[#7A5A2B]">
                            {t.admin.hidden_badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-maquis-soft">{p.location} · {t.types[p.type]} · {p.guests} {t.locations_page.people}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 max-sm:flex-nowrap sm:gap-3">
                    <button
                      data-testid={`admin-hide-property-${p.slug}`}
                      onClick={() => toggleHidden(p)}
                      className="btn-outline-elegant max-sm:!px-3 max-sm:!py-1.5 max-sm:!text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                    >
                      {p.hidden ? t.admin.show : t.admin.hide}
                    </button>
                    <button
                      data-testid={`admin-edit-property-${p.slug}`}
                      onClick={() => setEditing(editing === p.id ? null : p.id)}
                      className="btn-outline-elegant max-sm:!px-3 max-sm:!py-1.5 max-sm:!text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                    >
                      {t.admin.edit}
                    </button>
                  </div>
                </div>
                {editing === p.id && (
                  <PropertyForm initial={propToForm(p)} onSave={saveProperty} onCancel={() => setEditing(null)} onDelete={() => deleteProperty(p.id)} propertyId={p.id} onBookingsChanged={() => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {})} t={t} />
                )}
              </div>
            ))}
          </section>
        )}

        {/* Propriétaires */}
        {tab === "owners" && (
          <section className="mt-10" data-testid="admin-owners-section">
            <div className="grid gap-10 lg:grid-cols-2">
              <div>
                <h2 className="eyebrow">{t.admin.clients_owners}</h2>
                {owners.length === 0 ? (
                  <p data-testid="admin-no-owners" className="mt-5 text-sm text-maquis-soft">{t.admin.no_owners}</p>
                ) : (
                  <ul className="mt-5 space-y-3">
                    {owners.map((o) => {
                      const open = expandedClient === `owner-${o.id}`;
                      return (
                        <li key={o.id} data-testid={`admin-owner-${o.email}`} className="rounded-md border border-maquis-deep/10 bg-casse text-sm">
                          <button
                            data-testid={`admin-client-toggle-owner-${o.id}`}
                            onClick={() => setExpandedClient(open ? null : `owner-${o.id}`)}
                            className="flex w-full items-center justify-between px-5 py-4 text-left"
                          >
                            <p className="font-medium text-maquis-deep">{o.name || o.email}</p>
                            <ChevronDown size={15} strokeWidth={1.5} className={`text-maquis-soft transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                          </button>
                          {open && (
                            <div className="border-t border-maquis-deep/10 px-5 py-4 text-sm text-maquis-olive" data-testid={`admin-client-detail-owner-${o.id}`}>
                              <p>{o.email}</p>
                              <p className="mt-1 text-xs text-maquis-soft">
                                {(o.property_ids || []).map((pid) => properties.find((p) => p.id === pid)?.name).filter(Boolean).join(", ") || "—"}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-4">
                                <button
                                  data-testid={`admin-client-edit-owner-${o.id}`}
                                  onClick={() => setEditingClient({ kind: "owner", id: o.id, name: o.name || "", email: o.email, phone: o.phone || "" })}
                                  className="inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-0.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
                                >
                                  <Pencil size={12} strokeWidth={1.75} /> {t.admin.client_edit}
                                </button>
                                <button
                                  data-testid={`admin-client-delete-owner-${o.id}`}
                                  onClick={() => deleteClient("owner", o.id)}
                                  className="inline-flex items-center gap-2 border-b border-[#B08D57]/50 pb-0.5 text-[11px] uppercase tracking-[0.12em] text-[#7A5A2B] transition-colors hover:border-[#7A5A2B]"
                                >
                                  <Trash2 size={12} strokeWidth={1.75} /> {t.admin.client_delete}
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <h2 className="eyebrow">{t.admin.clients_guests}</h2>
                {(() => {
                  const seen = new Set();
                  const guests = bookings.filter((b) => {
                    const k = (b.email || "").toLowerCase();
                    if (!k || seen.has(k)) return false;
                    seen.add(k);
                    return true;
                  });
                  return guests.length === 0 ? (
                    <p data-testid="admin-no-guests" className="mt-5 text-sm text-maquis-soft">{t.admin.no_guests}</p>
                  ) : (
                    <ul className="mt-5 space-y-3">
                      {guests.map((g) => {
                        const open = expandedClient === `guest-${g.id}`;
                        return (
                          <li key={g.id} data-testid={`admin-guest-${g.id}`} className="rounded-md border border-maquis-deep/10 bg-casse text-sm">
                            <button
                              data-testid={`admin-client-toggle-guest-${g.id}`}
                              onClick={() => setExpandedClient(open ? null : `guest-${g.id}`)}
                              className="flex w-full items-center justify-between px-5 py-4 text-left"
                            >
                              <p className="font-medium text-maquis-deep">{g.first_name} {g.last_name}</p>
                              <ChevronDown size={15} strokeWidth={1.5} className={`text-maquis-soft transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                            </button>
                            {open && (
                              <div className="border-t border-maquis-deep/10 px-5 py-4 text-sm text-maquis-olive" data-testid={`admin-client-detail-guest-${g.id}`}>
                                <p>{g.email} · {g.phone}</p>
                                <p className="mt-1 text-xs text-maquis-soft">
                                  <span className="uppercase tracking-wide">{t.admin.sim_arrival}</span> {g.arrival} · <span className="uppercase tracking-wide">{t.admin.sim_departure}</span> {g.departure} · {g.property_name || "—"} · {g.guests} pers.
                                </p>
                                {g.message && <p className="mt-2 italic text-maquis-olive">« {g.message} »</p>}
                                <div className="mt-3 flex flex-wrap items-center gap-4">
                                  <button
                                    data-testid={`admin-client-edit-guest-${g.id}`}
                                    onClick={() => setEditingClient({ kind: "guest", id: g.id, oldEmail: g.email, first_name: g.first_name || "", last_name: g.last_name || "", email: g.email, phone: g.phone || "" })}
                                    className="inline-flex items-center gap-2 border-b border-maquis-deep/30 pb-0.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
                                  >
                                    <Pencil size={12} strokeWidth={1.75} /> {t.admin.client_edit}
                                  </button>
                                  <button
                                    data-testid={`admin-client-delete-guest-${g.id}`}
                                    onClick={() => deleteClient("guest", g.email)}
                                    className="inline-flex items-center gap-2 border-b border-[#B08D57]/50 pb-0.5 text-[11px] uppercase tracking-[0.12em] text-[#7A5A2B] transition-colors hover:border-[#7A5A2B]"
                                  >
                                    <Trash2 size={12} strokeWidth={1.75} /> {t.admin.client_delete}
                                  </button>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>
            </div>
          </section>
        )}

        {/* Avis */}
        {tab === "reviews" && (
          <section className="mt-10" data-testid="admin-reviews-section">
            {reviews.length === 0 ? (
              <p data-testid="admin-no-reviews" className="text-sm text-maquis-soft">{t.admin.no_reviews}</p>
            ) : (
              <ul className="space-y-4">
                {reviews.map((r) => (
                  <li key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-md border border-maquis-deep/10 bg-casse p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-maquis-deep">
                          {r.first_name} {r.last_name}
                          <span className="ml-2 text-xs font-normal text-maquis-soft">— {r.property_name || "—"}</span>
                        </p>
                        <div className="mt-1 flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} size={13} strokeWidth={1.25} className={s <= r.rating ? "fill-[#B08D57] text-[#B08D57]" : "text-sable"} />
                          ))}
                        </div>
                      </div>
                      <button
                        data-testid={`admin-review-delete-${r.id}`}
                        onClick={() => deleteReview(r.id)}
                        className="border-b border-maquis-deep/30 pb-0.5 text-xs text-maquis-soft transition-colors hover:border-maquis-deep hover:text-maquis-deep"
                      >
                        {t.admin.delete}
                      </button>
                    </div>
                    {r.message && <p className="mt-3 text-sm leading-relaxed text-maquis-olive">« {r.message} »</p>}
                    <p className="mt-2 text-xs text-maquis-soft">{r.created_at?.slice(0, 10)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Statistiques */}
        {tab === "stats" && <StatsTab t={t} bookings={bookings} />}

        {/* Messages */}
        {tab === "contacts" && (
          <section className="mt-10" data-testid="admin-contacts-section">
            {selMessages.length > 0 && (
              <div data-testid="admin-messages-bulkbar" className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-maquis-deep/15 bg-ivoire px-5 py-3">
                <span data-testid="admin-messages-selected-count" className="text-xs uppercase tracking-[0.12em] text-maquis-deep">
                  {selMessages.length} {t.admin.selected_count}
                </span>
                <button
                  data-testid="admin-messages-bulk-delete"
                  onClick={() => bulkDelete(selMessages, () => setSelMessages([]))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-900/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-red-900/80 transition-colors duration-200 hover:bg-red-900 hover:text-casse"
                >
                  <Trash2 size={12} strokeWidth={1.75} /> {t.admin.delete_selected}
                </button>
                <button
                  data-testid="admin-messages-bulk-clear"
                  onClick={() => setSelMessages([])}
                  className="border-b border-maquis-deep/30 pb-0.5 text-xs text-maquis-deep transition-colors duration-200 hover:border-maquis-deep"
                >
                  {t.admin.clear_selection}
                </button>
              </div>
            )}
            {contacts.length === 0 ? (
              <p data-testid="admin-no-contacts" className="text-sm text-maquis-soft">{t.admin.no_contacts}</p>
            ) : (
              <ul className="space-y-4">
                {contacts.map((c) => (
                  <li key={c.id} data-testid={`admin-contact-${c.id}`} className="rounded-md border border-maquis-deep/10 bg-casse p-6">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-medium text-maquis-deep">{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || c.email}</p>
                      <div className="flex items-center gap-2.5">
                        <p className="text-xs text-maquis-soft">{c.created_at?.slice(0, 10)}</p>
                        <button
                          data-testid={`admin-contact-select-${c.id}`}
                          onClick={() => toggleSel(setSelMessages, { ...c, _kind: "contact" })}
                          aria-label="Sélectionner"
                          aria-pressed={selMessages.some((i) => i.id === c.id)}
                          className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-200 ${selMessages.some((i) => i.id === c.id) ? "border-maquis-deep bg-maquis-deep text-casse" : "border-maquis-deep/40 text-transparent hover:border-maquis-deep"}`}
                        >
                          <Check size={11} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-maquis-soft">
                      {t.owner.kind[c.kind || "autre"]} · {c.email}{c.phone ? ` · ${c.phone}` : ""}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-maquis-olive">{c.message}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <a
                        data-testid={`admin-contact-reply-${c.id}`}
                        href={`mailto:${c.email}?subject=${encodeURIComponent(t.admin.mail_subject_gestion)}`}
                        className="btn-outline-elegant inline-flex !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                      >
                        {t.admin.reply}
                      </a>
                      <select
                        data-testid={`admin-contact-status-${c.id}`}
                        value={c.status === "VALIDEE" || c.status === "REFUSEE" ? c.status : ""}
                        onChange={(e) => e.target.value && updateContactStatus(c.id, e.target.value)}
                        className="rounded-full border border-maquis-deep/25 bg-casse px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-maquis-deep transition-colors hover:border-maquis-deep"
                      >
                        <option value="" disabled>{t.owner.status_placeholder}</option>
                        <option value="VALIDEE">{t.owner.accept}</option>
                        <option value="REFUSEE">{t.owner.refuse}</option>
                      </select>
                      <button
                        data-testid={`admin-contact-delete-${c.id}`}
                        onClick={() => deleteMessage(c.id)}
                        className="inline-flex items-center gap-1.5 text-xs text-maquis-soft transition-colors duration-200 hover:text-red-900"
                      >
                        <Trash2 size={12} strokeWidth={1.75} /> {t.admin.delete}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        </>
        )}

        {/* Modale détail demande */}
        {selectedBooking && (
          <div
            data-testid="booking-detail-overlay"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-mer/70 p-4 backdrop-blur-sm"
            onClick={() => setSelectedBooking(null)}
          >
            <div
              data-testid="booking-detail-modal"
              className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-md bg-casse p-8 shadow-[0_30px_80px_-30px_rgba(42,51,35,0.5)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="eyebrow">{selectedBooking._kind === "contact" ? t.owner.kind[selectedBooking.kind || "autre"] : t.admin.booking_details}</p>
                  <h3 className="mt-2 font-serif text-2xl text-maquis-deep">{selectedBooking.first_name} {selectedBooking.last_name}</h3>
                </div>
                <button data-testid="booking-detail-close" onClick={() => setSelectedBooking(null)} aria-label="Fermer" className="rounded-full p-2 text-maquis-soft transition-colors hover:bg-ivoire hover:text-maquis-deep">
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>
              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.owner.th_property}</dt>
                  <dd data-testid="booking-detail-property" className="font-medium text-maquis-deep">{selectedBooking.property_name || "—"}</dd>
                </div>
                {selectedBooking.arrival ? (
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.admin.sim_arrival}</dt>
                  <dd data-testid="booking-detail-arrival" className="font-medium text-maquis-deep">{selectedBooking.arrival}</dd>
                </div>
                ) : null}
                {selectedBooking.departure ? (
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.admin.sim_departure}</dt>
                  <dd data-testid="booking-detail-departure" className="font-medium text-maquis-deep">{selectedBooking.departure}</dd>
                </div>
                ) : null}
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.owner.th_guests}</dt>
                  <dd className="font-medium text-maquis-deep">{selectedBooking.guests || "—"}</dd>
                </div>
                {selectedBooking._kind === "contact" && (selectedBooking.property_type || selectedBooking.sector) ? (
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.gallery.place}</dt>
                  <dd className="font-medium text-maquis-deep">{[selectedBooking.property_type ? t.types[selectedBooking.property_type] : null, selectedBooking.sector].filter(Boolean).join(" · ")}</dd>
                </div>
                ) : null}
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.owner.th_status}</dt>
                  <dd><StatusBadge status={selectedBooking.status} /></dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">E-mail</dt>
                  <dd className="font-medium text-maquis-deep">{selectedBooking.email}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                  <dt className="text-maquis-soft">{t.contact.phone_label}</dt>
                  <dd className="font-medium text-maquis-deep">{selectedBooking.phone}</dd>
                </div>
                {[...selectedBooking.services, ...selectedBooking.providers].length > 0 && (
                  <div className="flex justify-between gap-4 border-b border-maquis-deep/5 pb-2">
                    <dt className="text-maquis-soft">{t.owner.th_services}</dt>
                    <dd className="text-right font-medium text-maquis-deep">{[...selectedBooking.services, ...selectedBooking.providers].join(", ")}</dd>
                  </div>
                )}
              </dl>
              {selectedBooking.message && (
                <div className="mt-5 rounded-md bg-ivoire p-4">
                  <p className="field-label">{t.contact.booking_message}</p>
                  <p data-testid="booking-detail-message" className="mt-2 text-sm leading-relaxed text-maquis-olive">« {selectedBooking.message} »</p>
                </div>
              )}
              {selectedBooking._kind !== "contact" && (
                <AssignProperty key={selectedBooking.id} t={t} booking={selectedBooking} properties={properties} onAssign={assignProperty} />
              )}
              {selectedBooking._kind !== "contact" && (user.email === CARO_EMAIL || user.email === MARGOT_EMAIL) && (
                <div className="mt-6 rounded-md border border-maquis-deep/10 bg-ivoire/60 p-4" data-testid="booking-commission">
                  <p className="field-label">{t.admin.commission_title}</p>
                  <p data-testid="commission-stay-total" className="mt-1 text-sm text-maquis-deep">
                    {t.admin.stay_total} : <strong>{(selectedBooking.stay_total || 0).toLocaleString("fr-FR")} €</strong>
                  </p>
                  {user.email === CARO_EMAIL && (
                    <div className="mt-3">
                      <label className="field-label">{t.admin.commission_pct_label}</label>
                      <div className="flex items-center gap-3">
                        <div className="relative w-32">
                          <input
                            type="number" min="0" max="100" step="0.5"
                            data-testid="commission-pct-input"
                            value={commissionInput}
                            onChange={(e) => setCommissionInput(e.target.value)}
                            className="field-input pr-8"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-maquis-soft">%</span>
                        </div>
                        <button
                          data-testid="commission-save"
                          onClick={() => saveCommission(selectedBooking.id, { commission_pct: Number(commissionInput) })}
                          className="btn-outline-elegant !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                        >
                          {t.admin.site_save}
                        </button>
                      </div>
                      {selectedBooking.commission_pct != null && (
                        <p data-testid="commission-amount" className="mt-2 text-sm font-semibold text-maquis-deep">
                          {t.admin.commission_amount} : {Math.round((selectedBooking.stay_total || 0) * selectedBooking.commission_pct / 100).toLocaleString("fr-FR")} €
                        </p>
                      )}
                    </div>
                  )}
                  {user.email === MARGOT_EMAIL && (
                    <div className="mt-3">
                      <label className="field-label">{t.admin.margot_pct_label} <span className="normal-case tracking-normal text-maquis-soft">({t.admin.margot_base_note})</span></label>
                      <div className="flex items-center gap-3">
                        <div className="relative w-32">
                          <input
                            type="number" min="0" max="100" step="0.5"
                            data-testid="margot-pct-input"
                            value={margotInput}
                            onChange={(e) => setMargotInput(e.target.value)}
                            className="field-input pr-8"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-maquis-soft">%</span>
                        </div>
                        <button
                          data-testid="margot-pct-save"
                          onClick={() => saveCommission(selectedBooking.id, { margot_pct: Number(margotInput) })}
                          className="btn-outline-elegant !px-4 !py-2 !text-[10px] text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                        >
                          {t.admin.site_save}
                        </button>
                      </div>
                      {selectedBooking.margot_pct != null && (
                        <div className="mt-3 flex items-center gap-3">
                          <button
                            role="checkbox"
                            aria-checked={!!selectedBooking.margot_paid}
                            data-testid="margot-paid-toggle"
                            onClick={() => saveCommission(selectedBooking.id, { margot_paid: !selectedBooking.margot_paid })}
                            className={`flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${selectedBooking.margot_paid ? "border-maquis-olive bg-maquis-olive text-casse" : "border-maquis-deep/40 text-transparent hover:border-maquis-olive"}`}
                          >
                            <Check size={13} strokeWidth={2.5} />
                          </button>
                          <p data-testid="margot-amount" className="text-sm font-semibold text-maquis-deep">
                            {Math.round((selectedBooking.stay_total || 0) * (selectedBooking.commission_pct || 0) / 100 * selectedBooking.margot_pct / 100).toLocaleString("fr-FR")} € — {t.admin.margot_paid}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <a
                data-testid="booking-detail-reply"
                href={`mailto:${selectedBooking.email}?subject=${encodeURIComponent(t.admin.mail_subject_location)}`}
                className="btn-outline-elegant mt-6 inline-flex text-maquis-deep hover:bg-maquis-deep hover:text-casse"
              >
                {t.admin.reply}
              </a>
              <button
                data-testid="booking-detail-delete"
                onClick={() => deleteDemande(selectedBooking)}
                className="ml-4 mt-6 inline-flex items-center gap-1.5 border-b border-red-900/30 pb-0.5 text-xs text-red-900/70 transition-colors duration-200 hover:border-red-900 hover:text-red-900"
              >
                <Trash2 size={12} strokeWidth={1.75} /> {t.admin.delete}
              </button>
            </div>
          </div>
        )}

        {editingClient && (
          <div
            data-testid="client-edit-overlay"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-mer/70 p-4 backdrop-blur-sm"
            onClick={() => setEditingClient(null)}
          >
            <div
              data-testid="client-edit-modal"
              className="w-full max-w-md rounded-md bg-casse p-8 shadow-[0_30px_80px_-30px_rgba(42,51,35,0.5)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-serif text-2xl text-maquis-deep">{t.admin.client_edit}</h3>
                <button data-testid="client-edit-close" onClick={() => setEditingClient(null)} aria-label="Fermer" className="rounded-full p-2 text-maquis-soft transition-colors hover:bg-ivoire hover:text-maquis-deep">
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>
              <div className="mt-6 space-y-4">
                {editingClient.kind === "guest" ? (
                  <>
                    <div>
                      <label className="field-label">{t.admin.tenant_fn}</label>
                      <input data-testid="client-edit-fn" value={editingClient.first_name} onChange={(e) => setEditingClient((s) => ({ ...s, first_name: e.target.value }))} className="field-input" />
                    </div>
                    <div>
                      <label className="field-label">{t.admin.tenant_ln}</label>
                      <input data-testid="client-edit-ln" value={editingClient.last_name} onChange={(e) => setEditingClient((s) => ({ ...s, last_name: e.target.value }))} className="field-input" />
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="field-label">{t.admin.tenant_ln}</label>
                    <input data-testid="client-edit-name" value={editingClient.name} onChange={(e) => setEditingClient((s) => ({ ...s, name: e.target.value }))} className="field-input" />
                  </div>
                )}
                <div>
                  <label className="field-label">{t.admin.tenant_email}</label>
                  <input data-testid="client-edit-email" type="email" value={editingClient.email} onChange={(e) => setEditingClient((s) => ({ ...s, email: e.target.value }))} className="field-input" />
                </div>
                <div>
                  <label className="field-label">{t.admin.tenant_phone}</label>
                  <input data-testid="client-edit-phone" value={editingClient.phone} onChange={(e) => setEditingClient((s) => ({ ...s, phone: e.target.value }))} className="field-input" />
                </div>
              </div>
              <button
                data-testid="client-edit-save"
                onClick={saveClient}
                className="btn-outline-elegant mt-6 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
              >
                {t.admin.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
